import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Send, Check, AlertCircle, Lock } from "lucide-react";
import analytics from "@/utils/analytics";
import MaintenanceOverlay from "@/components/MaintenanceOverlay";
import {
  CodeBlockRenderer,
  CodeInputRenderer,
  ScenarioBriefRenderer,
  StructuredResponseRenderer,
  TAQuestionRenderer,
  DiagramUploadRenderer,
  TimerBar,
  GateLockScreen,
  SandboxSimulation,
} from "./renderers";
import { VoiceRecorder } from "./renderers/VoiceRecorder";
import { useIntegrityMonitor } from "./renderers/useIntegrityMonitor";
import { useActivityLogger } from "./hooks/useActivityLogger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Debounce hook for auto-save
function useDebounce(callback, delay) {
  const [timer, setTimer] = useState(null);
  
  const debouncedCallback = useCallback((...args) => {
    if (timer) clearTimeout(timer);
    const newTimer = setTimeout(() => {
      callback(...args);
    }, delay);
    setTimer(newTimer);
  }, [callback, delay, timer]);
  
  return debouncedCallback;
}

function DynamicAssessmentFormContent() {
  const { accessToken } = useParams();
  const navigate = useNavigate();
  const isPreviewMode = new URLSearchParams(window.location.search).get("preview") === "true";
  const isQAMode = new URLSearchParams(window.location.search).get("qa") === "true";
  const adminToken = new URLSearchParams(window.location.search).get("admin_token") || "";
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [timerActivated, setTimerActivated] = useState(false);
  
  const [assessmentData, setAssessmentData] = useState(null);
  const [responses, setResponses] = useState({});
  const [lastSaved, setLastSaved] = useState(null);
  const [certificationChecked, setCertificationChecked] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [signatureDate, setSignatureDate] = useState("");
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [extraTableRows, setExtraTableRows] = useState({});
  const [sectionTimedOut, setSectionTimedOut] = useState({});
  const [gateCheckPassed, setGateCheckPassed] = useState(false);
  const [activeActIndex, setActiveActIndex] = useState(0);

  // Detect TA/HOS assessment type
  const assessmentType = assessmentData?.candidate?.assessmentType;
  const isTimedAssessment = assessmentType === "technical_assessment" || assessmentType === "hands_on_simulation";
  const isVoiceEnabled = isTimedAssessment;
  const candidateId = assessmentData?.candidate?.id;
  const totalTimeSeconds = assessmentData?.candidate?.timeLimitTotal 
    || assessmentData?.template?.documentStructure?.metadata?.total_time_seconds
    || null;
  const simulationMode = assessmentData?.template?.documentStructure?.metadata?.simulation_mode;
  const isHOSFormMode = assessmentType === "hands_on_simulation" && simulationMode === "form";
  const isBrowserSimulation = assessmentType === "hands_on_simulation" && simulationMode === "browser_simulation";
  const actTimeLimitSeconds = 360;
  const isTAPhaseMode = assessmentType === "technical_assessment";
  const [activePhaseIndex, setActivePhaseIndex] = useState(0);

  // Phase 5: Integrity monitoring for TA/HOS assessments
  const { trackPaste, trackKeystroke, flush: flushIntegrity } = useIntegrityMonitor(
    candidateId,
    isTimedAssessment
  );

  // Phase 6: Comprehensive activity logging
  const activity = useActivityLogger(accessToken, !isPreviewMode);

  // Add extra rows to a table
  const addTableRows = (tableId, currentRowCount, numToAdd = 5) => {
    setExtraTableRows(prev => ({
      ...prev,
      [tableId]: (prev[tableId] || 0) + numToAdd
    }));
  };

  // Check if document has embedded certification section
  const documentContent = assessmentData?.template?.documentStructure?.content || [];
  
  // Filter out certification-related items from content rendering
  // These will be handled by the hardcoded certification section
  const isCertificationItem = (item, index, allContent) => {
    // Check if item text contains certification keywords
    const textLower = (item.text || '').toLowerCase();
    const headersStr = (item.headers || []).join(' ').toLowerCase();
    
    // For tables, also check the first header cell (used for single-cell description tables)
    const firstHeaderLower = (item.headers && item.headers[0] || '').toLowerCase();
    
    // Items to filter out:
    // - Section headers with "CERTIFICATION" text
    // - Tables/headers with "CERTIFICATION" in them
    // - Key-value tables with signature/date fields
    // - Tables with checkbox symbols (☐) for certification checkboxes
    // - "THANK YOU" messages at the end
    // - Paragraphs about certification/attestation
    // - Assessment ID/Employee info blocks at the end
    // - Manager info lines at the end
    
    // Filter section_header items with certification text
    if (item.type === 'section_header' && textLower.includes('certification')) return true;
    if (item.style === 'certification') return true;
    
    // Filter any item with text that is exactly "CERTIFICATION" or similar
    if (textLower.trim() === 'certification') return true;
    
    // Don't filter data entry tables for listing professional certifications
    // These have headers like "Certification Name", "Issuing Body", etc.
    if (item.type === 'table' && headersStr.includes('certification name')) return false;
    if (item.style === 'data_entry' && headersStr.includes('certification')) return false;
    if (item.table_type === 'certification_table') return false;
    
    // Filter only attestation/signing certification sections
    if (headersStr.includes('i certify') || headersStr.includes('attestation')) return true;
    if (headersStr.includes('signature:') || headersStr.includes('date:')) return true;
    if (headersStr.includes('employee name:') && item.style === 'key_value') return true;
    if (headersStr.includes('☐') && headersStr.includes('certify')) return true;
    if (headersStr.includes('thank you') && headersStr.includes('responses')) return true;
    
    // Check text content
    if (textLower.includes('by signing below') || textLower.includes('i certify that')) return true;
    if (textLower.includes('i understand that this assessment is being conducted')) return true;
    if (textLower.includes('i acknowledge that providing inaccurate')) return true;
    if (textLower.includes('thank you for completing')) return true;
    if (textLower.includes('your input is valuable')) return true;
    if (textLower.includes('your contributions')) return true;
    
    // Check first header cell (for single-cell description tables rendered as blue boxes)
    if (firstHeaderLower.includes('i certify that')) return true;
    if (firstHeaderLower.includes('by signing below')) return true;
    if (firstHeaderLower.includes('thank you for completing')) return true;
    if (firstHeaderLower.includes('your input is valuable')) return true;
    if (firstHeaderLower.includes('your contributions')) return true;
    
    // Filter Assessment ID / Employee info blocks at the end (usually in paragraphs or tables)
    if (textLower.includes('assessment id:') && textLower.includes('employee:')) return true;
    if (headersStr.includes('assessment id:') && headersStr.includes('employee:')) return true;
    if (firstHeaderLower.includes('assessment id:') && firstHeaderLower.includes('employee:')) return true;
    
    // Filter Manager info lines at the end
    if (textLower.startsWith('manager:') && textLower.includes('@')) return true;
    if (headersStr.startsWith('manager:') && headersStr.includes('@')) return true;
    if (firstHeaderLower.startsWith('manager:') && firstHeaderLower.includes('@')) return true;
    
    return false;
  };
  
  // Always use hardcoded certification for proper form inputs
  const hasEmbeddedCertification = false;

  // Fetch assessment data
  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        setLoading(true);
        const previewParam = isPreviewMode ? "?preview=true" : "";
        const qaParam = isQAMode ? `${previewParam ? "&" : "?"}qa=true&admin_token=${adminToken}` : "";
        const response = await axios.get(`${API}/dynamic-assessments/take/${accessToken}${previewParam}${qaParam}`);
        setAssessmentData(response.data);
        setResponses(response.data.existingResponses || {});
        setProgressPercentage(response.data.progressPercentage || 0);
        
        // Start analytics session
        analytics.startSession({
          type: 'candidate',
          id: response.data.candidate?.id,
          email: response.data.candidate?.email,
          name: response.data.candidate?.fullName,
          progress: response.data.progressPercentage || 0
        });
        analytics.trackPageView('Assessment Form');
      } catch (err) {
        console.error("Error fetching assessment:", err);
        setError(err.response?.data?.detail || "Failed to load assessment");
        analytics.trackError(err.response?.data?.detail || "Failed to load assessment", err.stack);
      } finally {
        setLoading(false);
      }
    };
    
    if (accessToken) {
      fetchAssessment();
    }
  }, [accessToken]);

  // Gate enforcement: TA/HOS must go through TALandingPage (email + mic test) first
  useEffect(() => {
    if (!assessmentData || isPreviewMode) return;
    const aType = assessmentData.candidate?.assessmentType;
    if (aType === 'technical_assessment' || aType === 'hands_on_simulation') {
      const verified = sessionStorage.getItem(`ta_verified_${accessToken}`);
      if (!verified) {
        navigate(`/ta-assessment/${accessToken}`, { replace: true });
      }
    }
  }, [assessmentData, accessToken, isPreviewMode, navigate]);

  // Auto-save snapshots every 30 seconds for audit trail
  useEffect(() => {
    if (!accessToken || !isTimedAssessment || !candidateId) return;
    const interval = setInterval(async () => {
      const entries = Object.entries(responses);
      if (entries.length === 0) return;
      const snapshots = entries.slice(0, 20).map(([key, val]) => {
        const parts = key.split("__");
        const sectionId = parts.length > 1 ? parts[0] : "default";
        const text = typeof val === "string" ? val : JSON.stringify(val);
        return { question_id: key, section_id: sectionId, response_text: text, typed_chars: text.length };
      });
      try {
        await fetch(`${API}/assessment/${accessToken}/snapshot`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ snapshots }),
        });
      } catch { /* non-blocking */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [accessToken, isTimedAssessment, candidateId, responses]);

  // Auto-save handler
  const saveResponses = async (responsesToSave) => {
    if (!accessToken || Object.keys(responsesToSave).length === 0) return;
    if (isQAMode || isPreviewMode) return; // QA/Preview mode: no saves
    
    try {
      setSaving(true);
      const result = await axios.post(`${API}/dynamic-assessments/take/${accessToken}/save`, responsesToSave);
      setLastSaved(new Date());
      // Update progress percentage from server response
      if (result.data?.progressPercentage !== undefined) {
        setProgressPercentage(result.data.progressPercentage);
      }
      // Activate deferred timers when first non-empty response is saved
      if (result.data?.timerActivated && !timerActivated) {
        setTimerActivated(true);
      }
    } catch (err) {
      console.error("Error saving responses:", err);
    } finally {
      setSaving(false);
    }
  };
  
  const debouncedSave = useDebounce(saveResponses, 2000);

  // Handle response change
  const handleResponseChange = (questionId, value) => {
    const newResponses = { ...responses, [questionId]: value };
    setResponses(newResponses);
    debouncedSave(newResponses);
  };

  // Integrity-aware response change (tracks typing for TA/HOS)
  const handleTrackedResponseChange = (questionId, value) => {
    const oldValue = responses[questionId] || "";
    const diff = (value?.length || 0) - (oldValue?.length || 0);
    if (diff > 0 && isTimedAssessment) {
      trackKeystroke(questionId, diff);
    }
    activity.logResponseChange(null, questionId, value?.length || 0);
    handleResponseChange(questionId, value);
  };

  // Paste handler for integrity tracking
  const handleTrackedPaste = (questionId, pastedText) => {
    if (isTimedAssessment) {
      trackPaste(questionId, pastedText);
    }
  };

  // Handle table cell change
  const handleTableCellChange = (tableId, rowIndex, colIndex, value) => {
    const key = `${tableId}_${rowIndex}_${colIndex}`;
    handleResponseChange(key, value);
  };

  // Submit assessment
  const handleSubmit = async () => {
    // If using hardcoded certification, require checkbox and signature
    if (!hasEmbeddedCertification) {
      if (!certificationChecked) {
        toast.error("Please certify that your information is accurate before submitting");
        return;
      }
      if (!signatureName.trim()) {
        toast.error("Please provide your signature (full name)");
        return;
      }
    }
    
    try {
      setSubmitting(true);
      activity.logFinalSubmit();
      
      // Phase 5: Flush integrity data before submit
      if (isTimedAssessment) {
        try { flushIntegrity(); } catch {}
      }
      // Save any pending responses first
      await axios.post(`${API}/dynamic-assessments/take/${accessToken}/save`, responses);
      // Submit the assessment
      await axios.post(`${API}/dynamic-assessments/take/${accessToken}/submit`, {
        certified: true,
        certifiedAt: signatureDate || new Date().toISOString(),
        signature: signatureName || assessmentData?.candidate?.fullName,
        candidateName: assessmentData?.candidate?.fullName
      });
      
      toast.success("Assessment submitted successfully!");
      setSubmitted(true);
    } catch (err) {
      console.error("Error submitting assessment:", err);
      toast.error(err.response?.data?.detail || "Failed to submit assessment");
    } finally {
      setSubmitting(false);
    }
  };

  // Manual save
  const handleManualSave = async () => {
    await saveResponses(responses);
    toast.success("Progress saved!");
  };

  // Submit a TA phase manually (same effect as timer expiry)
  const handlePhaseSubmit = (phaseId, phaseIdx, totalPhases) => {
    setSectionTimedOut(prev => ({ ...prev, [phaseId]: true }));
    saveResponses(responses);
    activity.logPhaseSubmit(phaseId, phaseIdx, "manual");
    toast.success(`Phase ${phaseIdx + 1} submitted`);
    if (phaseIdx + 1 < totalPhases) {
      setActivePhaseIndex(phaseIdx + 1);
    }
  };

  // Submit an HOS act manually (same effect as timer expiry)
  const handleActSubmit = (actId, actIdx, totalActs) => {
    setSectionTimedOut(prev => ({ ...prev, [actId]: true }));
    saveResponses(responses);
    activity.logPhaseSubmit(actId, actIdx, "manual");
    toast.success(`Act ${actIdx + 1} submitted`);
    if (actIdx + 1 < totalActs) {
      setActiveActIndex(actIdx + 1);
    }
  };

  // Check if table is a Likert/survey scale
  const isLikertTable = (headers) => {
    if (!headers || headers.length < 3) return false;
    const headerStr = headers.map(h => (h || '').trim().toUpperCase()).join('|');
    const likertPatterns = ['SD', 'D', 'N', 'A', 'SA', 'STRONGLY DISAGREE', 'DISAGREE', 'NEUTRAL', 'AGREE', 'STRONGLY AGREE'];
    const matchCount = likertPatterns.filter(p => headerStr.includes(p)).length;
    return matchCount >= 3;
  };

  // Render a table based on its style
  const renderTable = (item, index) => {
    const { headers = [], rows = [], style } = item;
    const tableId = `table_${index}`;
    
    // ===== MANAGER VALIDATION: Profile/read-only table detection =====
    // Tables with 2+ column key-value rows containing known profile fields
    const profileLabels = ['assessment id', 'tca id', 'employee name', 'employee full name', 'employee email',
      'job title', 'department', 'grade', 'job grade', 'grade / level', 'region', 'region / entity', 'location',
      'validating manager', 'manager email', 'manager title', 'hire date', 'tenure', 'tenure at sagicor',
      'prior performance rating', 'performance rating', 'reporting manager', 'direct manager',
      'full name', 'email', 'operating entity', 'operating entities', 'business unit', 'work arrangement',
      'years at sagicor', 'current role since', 'department head', 'education', 'certifications', 'direct manager name',
      'direct manager email'];
    const allTableRows = headers.length > 0 ? [headers, ...rows] : rows;
    // Support both 2-column and wider tables (ignore extra empty columns)
    const isProfileTable = allTableRows.length >= 3 && allTableRows.every(r => r.length >= 2) &&
      allTableRows.filter(r => profileLabels.includes((r[0] || '').toLowerCase().trim())).length >= 2;
    
    if (isProfileTable) {
      return (
        <div key={index} className="my-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {allTableRows.map((row, rowIdx) => {
              if (row.length >= 2 && row[0]) {
                // Skip rows where both cells have the same text (duplicate header title)
                if (row[0].trim() === row[1]?.trim()) return null;
                // Treat underscore-only values as blank (needs input)
                const cellValue = (row[1] || '').trim();
                const isBlankValue = !cellValue || cellValue === '—' || /^_+$/.test(cellValue);
                const inputKey = `${tableId}_${rowIdx}_1`;
                return (
                  <div key={rowIdx} className="flex items-center bg-white border rounded-lg p-3">
                    <span className="font-medium text-[#3b3b3b] min-w-[180px] text-sm">{row[0]}</span>
                    {isBlankValue ? (
                      <Input
                        className="flex-1 h-8 text-sm"
                        placeholder={`Enter ${row[0].replace(':', '').trim()}`}
                        value={responses[inputKey] || ""}
                        onChange={(e) => handleResponseChange(inputKey, e.target.value)}
                        data-testid={`profile-input-${rowIdx}`}
                      />
                    ) : (
                      <span className="text-gray-700 text-sm">{row[1]}</span>
                    )}
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      );
    }
    
    // ===== MULTI-COLUMN CHECKBOX LIST detection =====
    // Pattern: 4-col tables where cols 0,2 are ☐ and cols 1,3 are option text
    // e.g. ['☐', 'Public acknowledgment', '☐', 'Private praise']
    const isMultiColCheckboxList = headers.length === 4 &&
      (headers[0] || '').trim() === '☐' && (headers[2] || '').trim() === '☐' &&
      (headers[1] || '').trim().length > 1 && (headers[3] || '').trim().length > 1;
    
    if (isMultiColCheckboxList) {
      // Collect all checkbox options from headers + rows
      const options = [];
      const allRows = [headers, ...rows];
      allRows.forEach(row => {
        // Each row has pairs: [☐, text, ☐, text]
        if ((row[1] || '').trim()) options.push((row[1] || '').trim());
        if ((row[3] || '').trim()) options.push((row[3] || '').trim());
      });
      
      return (
        <div key={index} className="my-3" data-testid={`multi-checkbox-${index}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {options.map((opt, optIdx) => {
              const optKey = `${tableId}_multicheck_${optIdx}`;
              const isOtherField = opt.toLowerCase().includes('other:') || opt.includes('___');
              return (
                <label key={optIdx} className="flex items-start gap-3 p-3 bg-white border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors" data-testid={`multi-checkbox-option-${index}-${optIdx}`}>
                  <input
                    type="checkbox"
                    checked={responses[optKey] === true}
                    onChange={(e) => handleResponseChange(optKey, e.target.checked)}
                    className="w-5 h-5 mt-0.5 accent-[#00AEEF] cursor-pointer flex-shrink-0"
                    style={{pointerEvents: 'auto', position: 'relative', zIndex: 2}}
                  />
                  {isOtherField ? (
                    <div className="flex flex-col gap-1 flex-1">
                      <span className="text-sm text-[#3b3b3b]">Other</span>
                      <Input
                        className="h-8 text-sm"
                        placeholder="Please specify..."
                        value={responses[`${optKey}_text`] || ""}
                        onChange={(e) => handleResponseChange(`${optKey}_text`, e.target.value)}
                        data-testid={`multi-checkbox-other-${index}`}
                      />
                    </div>
                  ) : (
                    <span className="text-sm text-[#3b3b3b]">{opt}</span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      );
    }
    
    // ===== MANAGER VALIDATION: ICARE / rating grid detection =====
    // Tables where rows have ☐ symbols in cells (radio selection per row)
    const rowsHaveCheckboxes = rows.length > 0 && rows.every(r => 
      r.length >= 3 && r.slice(1).some(cell => (cell || '').includes('☐'))
    );
    const headersHaveRatingLabels = headers.some(h => {
      const hl = (h || '').toLowerCase();
      return hl.includes('always') || hl.includes('usually') || hl.includes('sometimes') || 
             hl.includes('rarely') || hl.includes('not observed') || hl.includes('never') ||
             hl.includes('strongly') || hl.includes('agree') || hl.includes('disagree');
    });
    const isICAREGrid = rowsHaveCheckboxes && (headersHaveRatingLabels || headers.length >= 4);
    
    if (isICAREGrid) {
      // Identify comment/text columns (non-checkbox, non-label columns)
      const commentColIndices = headers.map((h, idx) => {
        if (idx === 0) return false;
        const hl = (h || '').toLowerCase();
        return hl.includes('comment') || hl.includes('note') || hl.includes('remark');
      });
      
      const hasCommentCol = commentColIndices.some(Boolean);
      const firstColWidth = hasCommentCol ? 'w-[35%]' : 'w-[45%]';
      return (
        <div key={index} className="my-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm" data-rating-grid="true">
            <thead>
              <tr className="bg-[#00AEEF]/10">
                {headers.map((h, idx) => (
                  <th key={idx} className={`p-2 font-medium text-[#3b3b3b] border border-gray-200 text-xs ${
                    idx === 0 ? `text-left ${firstColWidth}` : commentColIndices[idx] ? 'text-left min-w-[100px]' : 'text-center min-w-[55px]'
                  }`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <tr key={rowIdx} className="hover:bg-gray-50">
                  {row.map((cell, colIdx) => {
                    const cellStr = (cell || '').trim();
                    const isCheckboxCell = cellStr.includes('☐');
                    const isCommentCol = commentColIndices[colIdx];
                    return (
                      <td key={colIdx} className={`p-2 border border-gray-200 ${
                        colIdx === 0 ? 'text-sm text-[#3b3b3b] whitespace-normal' : 'text-center'
                      }`}>
                        {colIdx === 0 ? (
                          <span className="text-sm">{cell}</span>
                        ) : isCommentCol ? (
                          <Input
                            className="h-8 text-sm w-full"
                            value={responses[`${tableId}_${rowIdx}_comment`] || ""}
                            onChange={(e) => handleResponseChange(`${tableId}_${rowIdx}_comment`, e.target.value)}
                            placeholder="Optional comment..."
                            data-testid={`icare-comment-${rowIdx}`}
                          />
                        ) : isCheckboxCell ? (
                          <div className="flex justify-center">
                            <input
                              type="radio"
                              name={`${tableId}_row_${rowIdx}`}
                              checked={responses[`${tableId}_${rowIdx}_selection`] === colIdx.toString()}
                              onChange={() => handleResponseChange(`${tableId}_${rowIdx}_selection`, colIdx.toString())}
                              className="w-5 h-5 accent-[#00AEEF] cursor-pointer"
                              data-testid={`icare-radio-${rowIdx}-${colIdx}`}
                            />
                          </div>
                        ) : (
                          <span className="text-sm">{cell}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    
    // ===== MANAGER VALIDATION: Scoring table detection =====
    // Tables with "Score" or "/ 5" in headers and skill domain rows
    const headersStr = (headers || []).join(' ').toLowerCase();
    const hasScoreHeader = headersStr.includes('score') || headersStr.includes('/ 5') || headersStr.includes('1-5') || headersStr.includes('rating');
    const hasWeightHeader = headersStr.includes('weight') || headersStr.includes('%');
    const hasSkillDomain = headersStr.includes('skill') || headersStr.includes('domain') || headersStr.includes('competency');
    const isScoringTable = hasScoreHeader && (hasWeightHeader || hasSkillDomain) && rows.length >= 2;
    
    if (isScoringTable) {
      // Identify column types
      const colTypes = headers.map(h => {
        const hl = (h || '').toLowerCase();
        if (hl.includes('skill') || hl.includes('domain') || hl.includes('competency')) return 'label';
        if (hl.includes('description') || hl.includes('focus')) return 'description';
        if (hl.includes('employee') || hl.includes('sa ')) return 'readonly';
        if (hl.includes('manager') && (hl.includes('score') || hl.includes('1-5') || hl.includes('rating'))) return 'score_input';
        if (hl.includes('score') || hl.includes('1-5') || hl.includes('rating')) return 'score_input';
        if (hl.includes('weight') || hl.includes('%')) return 'readonly';
        if (hl.includes('comment') || hl.includes('note') || hl.includes('remark')) return 'comment_input';
        return 'text';
      });
      
      return (
        <div key={index} className="my-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-[#00AEEF]/10">
                {headers.map((h, idx) => (
                  <th key={idx} className={`p-2 text-left font-medium text-[#3b3b3b] border border-gray-200 text-xs ${
                    colTypes[idx] === 'description' ? 'w-[30%]' : colTypes[idx] === 'label' ? 'w-[20%]' : ''
                  }`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => {
                const isTotal = (row[0] || '').toLowerCase().includes('total');
                return (
                  <tr key={rowIdx} className={isTotal ? 'bg-gray-100 font-medium' : rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {row.map((cell, colIdx) => {
                      const ct = colTypes[colIdx] || 'text';
                      return (
                        <td key={colIdx} className="p-2 border border-gray-200">
                          {ct === 'label' || ct === 'description' || ct === 'readonly' || isTotal ? (
                            <span className="text-sm text-[#3b3b3b]">{cell}</span>
                          ) : ct === 'score_input' ? (
                            <Input
                              type="number"
                              min="1"
                              max="5"
                              className="w-16 h-8 text-center text-sm"
                              value={responses[`${tableId}_${rowIdx}_${colIdx}`] || ""}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '' || (parseInt(v) >= 1 && parseInt(v) <= 5)) {
                                  handleResponseChange(`${tableId}_${rowIdx}_${colIdx}`, v);
                                }
                              }}
                              placeholder="1-5"
                              data-testid={`score-input-${rowIdx}-${colIdx}`}
                            />
                          ) : ct === 'comment_input' ? (
                            <Input
                              className="h-8 text-sm w-full"
                              value={responses[`${tableId}_${rowIdx}_comment`] || ""}
                              onChange={(e) => handleResponseChange(`${tableId}_${rowIdx}_comment`, e.target.value)}
                              placeholder="Optional comment..."
                              data-testid={`score-comment-${rowIdx}`}
                            />
                          ) : (
                            <Input
                              className="h-8 text-sm"
                              value={responses[`${tableId}_${rowIdx}_${colIdx}`] || cell || ""}
                              onChange={(e) => handleResponseChange(`${tableId}_${rowIdx}_${colIdx}`, e.target.value)}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    // ===== MANAGER VALIDATION: ICARE Frequency table (Value | Behaviours | Frequency | Comment) =====
    // New Canada format: no ☐ symbols, uses text-based frequency and comment columns
    const isICARFrequencyTable = headers.length >= 3 && 
      (headersStr.includes('value') || headersStr.includes('icare')) &&
      (headersStr.includes('frequency') || headersStr.includes('observable')) &&
      rows.length >= 3;
    
    if (isICARFrequencyTable) {
      const freqOptions = ['Always (5)', 'Usually (4)', 'Sometimes (3)', 'Rarely (2)', 'Not Observed (1)'];
      return (
        <div key={index} className="my-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm" data-testid={`icare-freq-table-${index}`}>
            <thead>
              <tr className="bg-[#00AEEF]/10">
                {headers.map((h, idx) => (
                  <th key={idx} className={`p-2 font-medium text-[#3b3b3b] border border-gray-200 text-xs ${
                    idx <= 1 ? 'text-left' : 'text-center'
                  } ${idx === 1 ? 'w-[35%]' : ''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {row.map((cell, colIdx) => {
                    const hl = (headers[colIdx] || '').toLowerCase();
                    const isFreqCol = hl.includes('frequency') || hl.includes('rating');
                    const isCommentCol = hl.includes('comment') || hl.includes('note');
                    return (
                      <td key={colIdx} className="p-2 border border-gray-200">
                        {isFreqCol ? (
                          <select
                            className="w-full h-8 text-sm border rounded px-2 bg-white"
                            value={responses[`${tableId}_${rowIdx}_freq`] || ""}
                            onChange={(e) => handleResponseChange(`${tableId}_${rowIdx}_freq`, e.target.value)}
                            data-testid={`icare-freq-select-${rowIdx}`}
                          >
                            <option value="">Select...</option>
                            {freqOptions.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : isCommentCol ? (
                          <Input
                            className="h-8 text-sm w-full"
                            value={responses[`${tableId}_${rowIdx}_comment`] || ""}
                            onChange={(e) => handleResponseChange(`${tableId}_${rowIdx}_comment`, e.target.value)}
                            placeholder="Optional..."
                            data-testid={`icare-freq-comment-${rowIdx}`}
                          />
                        ) : (
                          <span className="text-sm text-[#3b3b3b]">{cell}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // ===== MANAGER VALIDATION: Empty table after question → textarea =====
    const isEmpty = (!headers || headers.length === 0 || (headers.length === 1 && !headers[0])) && (!rows || rows.length === 0);
    // Also detect "Enter your response here..." tables (standard style with response prompt header)
    const isResponsePromptTable = !rows.length && headers.length === 1 && 
      (headers[0] || '').toLowerCase().includes('enter your response');
    
    if (isEmpty || isResponsePromptTable) {
      // Check if previous item was a question/paragraph — render as textarea
      const prevItem = content[index - 1];
      if (isResponsePromptTable || (prevItem && (prevItem.type === 'paragraph' || prevItem.type === 'question'))) {
        const prevText = prevItem ? (prevItem.text || '').toLowerCase() : '';
        if (isResponsePromptTable || prevText.startsWith('q') || prevText.includes('?') || prevText.includes('describe') || prevText.includes('explain') || prevText.includes('list') || prevText.includes('what') || prevText.includes('how')) {
          return (
            <div key={index} className="my-2 ml-4">
              <Textarea
                className="w-full min-h-[100px] text-sm"
                placeholder="Enter your response here..."
                value={responses[`${tableId}_text`] || ""}
                onChange={(e) => handleResponseChange(`${tableId}_text`, e.target.value)}
                data-testid={`textarea-${index}`}
              />
            </div>
          );
        }
      }
      // Skip truly empty tables
      return null;
    }
    
    // ===== Duplicate section header table suppression =====
    // Tables with single cell containing a section header that matches nearby content
    // EXCEPTION: Never suppress "[Your response here]" tables — each one is a unique text input field
    const isResponsePlaceholder = (headers[0] || '').toLowerCase().includes('your response here');
    if (headers.length === 1 && !rows.length && headers[0] && !isResponsePlaceholder) {
      const hText = headers[0].trim();
      // Check if the same text appeared in nearby tables (within 3 items)
      for (let si = Math.max(0, index - 3); si < index; si++) {
        const prevItem = content[si];
        if (prevItem?.type === 'table') {
          const prevHeaders = prevItem.headers || [];
          const prevRows = prevItem.rows || [];
          // Check if previous table had the same header text
          if (prevHeaders.length === 1 && prevHeaders[0]?.trim() === hText) {
            return null;
          }
          // Check if previous table had a row containing this text
          if (prevRows.some(r => r.some(c => (c || '').trim() === hText))) {
            return null;
          }
        }
      }
    }
    
    // Suppress empty tables with just an empty header and no rows
    if (headers.length === 1 && !headers[0] && !rows.length) {
      return null;
    }
    
    // ===== MANAGER VALIDATION: Checkbox option table (☐ | Option Text [| Description]) =====
    // Canada forms use tables where first col is ☐ and remaining cols are option text
    // These should render as radio button groups (single-select)
    // Supports both 2-col (☐ | text) and 3-col (☐ | name | description) formats
    const isCheckboxOptionTable = headers.length >= 2 && 
      (headers[0] || '').trim() === '☐' && 
      headers[1] && headers[1].length > 2 &&
      rows.every(r => r.length >= 2 && (r[0] || '').trim() === '☐');
    
    if (isCheckboxOptionTable) {
      // Combine all options from headers and rows (skip first ☐ column)
      const buildOptionText = (cols) => {
        const parts = cols.slice(1).filter(c => c && c.trim());
        return parts.join(' — ');
      };
      const allOptions = [buildOptionText(headers), ...rows.map(r => buildOptionText(r))];
      const radioId = `${tableId}_option_selection`;
      return (
        <div key={index} className="my-4 space-y-1 ml-4" data-testid={`radio-option-table-${index}`}>
          {allOptions.map((optText, optIdx) => {
            const isSelected = responses[radioId] === optText;
            const isConditional = (optText || '').toLowerCase().includes('yes') && 
              (optText || '').toLowerCase().includes('context');
            return (
              <div key={optIdx}>
                <div className="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                  <input
                    type="radio"
                    id={`${tableId}_opt_${optIdx}`}
                    name={radioId}
                    checked={isSelected}
                    onChange={() => handleResponseChange(radioId, optText)}
                    className="w-4 h-4 accent-[#00AEEF] cursor-pointer flex-shrink-0"
                    data-testid={`checkbox-table-radio-${index}-${optIdx}`}
                  />
                  <label htmlFor={`${tableId}_opt_${optIdx}`} className="cursor-pointer text-[#3b3b3b] text-sm">
                    {optText}
                  </label>
                </div>
                {isConditional && isSelected && (
                  <div className="ml-9 mt-1 mb-2">
                    <Textarea
                      className="w-full min-h-[80px] text-sm"
                      placeholder="Please provide additional details..."
                      value={responses[`${radioId}_detail`] || ""}
                      onChange={(e) => handleResponseChange(`${radioId}_detail`, e.target.value)}
                      data-testid={`checkbox-table-detail-${index}`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    
    // Re-establish data entry table detection for the remaining generic table rendering
    const isDataEntryTable = (style === 'data_entry' || 
      item.table_type === 'certification_table' ||
      headersStr.includes('certification name') ||
      headersStr.includes('issuing body') ||
      headersStr.includes('issuing organization') ||
      headersStr.includes('credential') ||
      (headers.length >= 2 && (headersStr.includes('name') && (headersStr.includes('date') || headersStr.includes('body') || headersStr.includes('expir'))))) &&
      !headersStr.includes('manager certification');  // Exclude manager certification section
    
    const extraRows = extraTableRows[tableId] || 0;
    const baseRowCount = rows.length;
    const totalRows = isDataEntryTable ? Math.max(baseRowCount, 3) + extraRows : baseRowCount;
    
    // Text input table (single input for free-form response) - render as one textarea
    // Also detect tables with "[Your response here]" in header (may be stored as style "standard")
    const isYourResponseTable = headers.length === 1 && rows.length === 0 && 
      (headers[0] || '').toLowerCase().includes('your response here');
    if (style === "text_input" || isYourResponseTable) {
      return (
        <div key={index} className="my-4 ml-11">
          <Textarea
            className="w-full min-h-[100px] text-sm"
            placeholder="Enter your response here..."
            value={responses[`${tableId}_text`] || ""}
            onChange={(e) => handleResponseChange(`${tableId}_text`, e.target.value)}
            data-testid={`table-text-input-${index}`}
          />
        </div>
      );
    }
    
    // Single-cell intro/description table - display as styled paragraph (not editable)
    // These are tables with one header cell containing long descriptive text
    // Exclude section headers (they should use the dark navy bar style below)
    const headerStartsWithSection = headers.length === 1 && headers[0] && headers[0].trim().toUpperCase().startsWith('SECTION');
    if (headers.length === 1 && headers[0] && headers[0].length > 100 && (!rows || rows.length === 0) && !headerStartsWithSection) {
      return (
        <div key={index} className="my-4 bg-blue-50 border-l-4 border-[#00AEEF] p-4 rounded-r-lg">
          <p className="text-[#3b3b3b] text-sm leading-relaxed italic">{headers[0]}</p>
        </div>
      );
    }
    
    // ===== Section header table with description row =====
    // Tables like: header="SECTION 3D — Title", rows=[["Description text"]]
    const headerText = (headers.length === 1 && headers[0]) ? headers[0].trim() : '';
    const isSectionHeaderTable = headerText.toUpperCase().startsWith('SECTION') && rows.length <= 1;
    
    // Also detect 2-column section headers: ["3A", "Title\nDescription"]
    const isTwoColSectionHeader = headers.length === 2 && rows.length === 0 &&
      /^\d+[A-Za-z]?\+?$/.test((headers[0] || '').trim()) &&
      (headers[1] || '').length > 5;
    
    if (isTwoColSectionHeader) {
      const sectionCode = headers[0].trim();
      const titleParts = (headers[1] || '').split('\n');
      const sectionTitle = titleParts[0] || '';
      const sectionDesc = titleParts.slice(1).join(' ').trim();
      return (
        <div key={index} className="my-6" data-testid={`section-header-${index}`}>
          <div className={`bg-[#1B3A5C] text-white px-4 py-3 ${sectionDesc ? 'rounded-t-lg' : 'rounded-lg'} flex items-baseline gap-3`}>
            <span className="font-bold text-lg whitespace-nowrap">SECTION {sectionCode}</span>
            <span className="text-white/90 font-medium">{sectionTitle}</span>
          </div>
          {sectionDesc && (
            <div className="bg-[#1B3A5C]/5 border border-t-0 border-[#1B3A5C]/20 px-4 py-2 rounded-b-lg">
              <p className="text-[#3b3b3b] text-sm italic">{sectionDesc}</p>
            </div>
          )}
        </div>
      );
    }
    
    if (isSectionHeaderTable) {
      const descText = rows.length > 0 && rows[0].length > 0 ? rows[0][0] : '';
      // Parse "SECTION 3D Work Culture..." → label + title
      const shMatch = headerText.match(/^(SECTION\s+\S+)\s*[—–-]?\s*(.*)$/i);
      const shLabel = shMatch ? shMatch[1].toUpperCase() : '';
      const shTitle = shMatch ? shMatch[2] : headerText;
      return (
        <div key={index} className="my-6" data-testid={`section-header-${index}`}>
          <div className={`bg-[#1B3A5C] text-white px-4 py-3 ${descText ? 'rounded-t-lg' : 'rounded-lg'} flex items-baseline gap-3`}>
            {shLabel && <span className="font-bold text-lg whitespace-nowrap">{shLabel}</span>}
            <span className={shLabel ? "text-white/90 font-medium" : "font-bold text-lg"}>{shTitle}</span>
          </div>
          {descText && (
            <div className="bg-[#1B3A5C]/5 border border-t-0 border-[#1B3A5C]/20 px-4 py-2 rounded-b-lg">
              <p className="text-[#3b3b3b] text-sm italic">{descText}</p>
            </div>
          )}
        </div>
      );
    }
    
    // Short instruction/note tables (header with text < 100 chars, no rows, not a section)
    if (headers.length === 1 && headers[0] && !rows.length && headers[0].length > 20) {
      return (
        <div key={index} className="my-3 bg-amber-50/50 border-l-3 border-amber-300 px-4 py-2 rounded-r">
          <p className="text-[#3b3b3b] text-sm italic">{headers[0]}</p>
        </div>
      );
    }
    
    // Section overview table (SECTION 1 | SECTION 2 | ...) - display only
    if (style === "section_overview") {
      return (
        <div key={index} className="my-6">
          <div className="flex flex-wrap gap-2 justify-center bg-[#00AEEF]/5 p-4 rounded-lg border">
            {headers.map((header, idx) => {
              const parts = header.split('\n');
              return (
                <div key={idx} className="px-4 py-2 bg-white rounded-lg shadow-sm border text-center min-w-[140px]">
                  <div className="font-bold text-[#00AEEF] text-sm">{parts[0]}</div>
                  {parts[1] && <div className="text-[#3b3b3b] text-sm">{parts[1]}</div>}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    
    // Instructions table - display only
    if (style === "instructions") {
      const instructionText = headers[0] || "";
      const lines = instructionText.split('\n').filter(l => l.trim());
      return (
        <div key={index} className="my-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="font-bold text-[#3b3b3b] text-lg mb-4">INSTRUCTIONS</h3>
          {lines.slice(1).map((line, idx) => (
            <p key={idx} className="text-[#3b3b3b] mb-2 text-sm leading-relaxed">{line}</p>
          ))}
        </div>
      );
    }
    
    // Key-value table (Profile, Reporting Structure, etc.) - make empty values editable
    if (style === "key_value") {
      const allRows = [headers, ...rows];
      const tableId = `table_${index}`;
      
      // Check if this is a percentage allocation table
      const isPercentTable = allRows.some(row => 
        row.some(cell => cell?.includes('%') || cell?.includes('_____'))
      );
      
      if (isPercentTable) {
        // Render as percentage input table
        return (
          <div key={index} className="my-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {allRows.map((row, rowIdx) => {
                if (row.length >= 2 && row[0]) {
                  const isTotal = row[0].toLowerCase() === 'total';
                  const inputKey = `${tableId}_${rowIdx}_1`;
                  return (
                    <div key={rowIdx} className={`flex items-center justify-between bg-white border rounded-lg p-3 ${isTotal ? 'bg-gray-100 font-medium' : ''}`}>
                      <span className="font-medium text-[#3b3b3b] flex-1">{row[0]}</span>
                      {isTotal ? (
                        <span className="text-gray-700 ml-2">100%</span>
                      ) : (
                        <div className="flex items-center ml-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            className="w-20 h-8 text-center text-sm"
                            value={responses[inputKey] || ""}
                            onChange={(e) => handleResponseChange(inputKey, e.target.value)}
                            placeholder="0"
                            data-testid={`percent-input-${rowIdx}`}
                          />
                          <span className="ml-1 text-gray-500">%</span>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        );
      }
      
      return (
        <div key={index} className="my-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {allRows.map((row, rowIdx) => {
              if (row.length >= 2 && row[0]) {
                const hasValue = row[1] && row[1].trim() && row[1].trim() !== "—" && row[1].trim() !== "☐" && row[1].trim() !== "___" && !row[1].includes('____');
                const inputKey = `${tableId}_${rowIdx}_1`;
                return (
                  <div key={rowIdx} className="flex items-center bg-white border rounded-lg p-3" style={{position: 'relative', zIndex: 1}}>
                    <span className="font-medium text-[#3b3b3b] min-w-[180px]">{row[0]}</span>
                    {hasValue ? (
                      <span className="text-gray-700">{row[1]}</span>
                    ) : (
                      <Input
                        className="flex-1 h-8 text-sm"
                        style={{pointerEvents: 'auto', position: 'relative', zIndex: 2}}
                        placeholder={`Enter ${row[0].replace(':', '')}`}
                        value={responses[inputKey] || ""}
                        onChange={(e) => handleResponseChange(inputKey, e.target.value)}
                        data-testid={`kv-input-${rowIdx}`}
                      />
                    )}
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      );
    }
    
    // Company/Job Title table - render as editable fields
    if (style === "company_job") {
      // Headers are like: ["Company:", "", "Job Title:", ""]
      // Render as a two-column input form
      return (
        <div key={index} className="my-4 ml-11 bg-white border rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <Input
                className="w-full"
                placeholder="Enter company name"
                value={responses[`${tableId}_0_1`] || ""}
                onChange={(e) => handleResponseChange(`${tableId}_0_1`, e.target.value)}
                data-testid={`company-input-${index}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
              <Input
                className="w-full"
                placeholder="Enter job title"
                value={responses[`${tableId}_0_3`] || ""}
                onChange={(e) => handleResponseChange(`${tableId}_0_3`, e.target.value)}
                data-testid={`jobtitle-input-${index}`}
              />
            </div>
          </div>
        </div>
      );
    }
    
    // Check if this is a rating table (has Rating/Freq columns)
    const headersLower = headers.map(h => h.toLowerCase()).join(' ');
    // Check if table has numbered columns (1-5) for rating selection
    const hasNumberedColumns = headers.some(h => ['1', '2', '3', '4', '5'].includes(h.trim()));
    const isRatingTable = headersLower.includes('rating') || headersLower.includes('freq') || headersLower.includes('i use') || hasNumberedColumns;
    // Likert scale detection - includes full words and common abbreviations (SD=Strongly Disagree, D=Disagree, N=Neutral, A=Agree, SA=Strongly Agree)
    const headersUpperArr = headers.map(h => h.toUpperCase().trim());
    const hasLikertAbbreviations = headersUpperArr.includes('SD') && headersUpperArr.includes('SA') && 
      (headersUpperArr.includes('D') || headersUpperArr.includes('N') || headersUpperArr.includes('A'));
    const isLikertTable = headersLower.includes('strongly') || headersLower.includes('disagree') || headersLower.includes('agree') || hasLikertAbbreviations;
    
    // Check if this is a checkbox selection table (headers have ☐ symbols and no rows)
    // Pattern 1: ['Skill Name', 'Description', '☐', '☐', '☐', '☐', '☐'] - 7 headers
    // Pattern 2: ['Item Name', '☐', '☐', '☐', '☐', '☐'] - 6 headers (no description)
    const checkboxHeaders = headers.filter(h => h === '☐' || h.includes('☐'));
    const isCheckboxSelectionTable = style === "standard" && 
      rows.length === 0 && 
      headers.length >= 3 && 
      checkboxHeaders.length >= 2;
    
    // Checkbox selection table - render as radio button selections
    if (isCheckboxSelectionTable) {
      // Find where checkboxes start
      const firstCheckboxIndex = headers.findIndex(h => h === '☐' || h.includes('☐'));
      const itemName = headers[0] || "";
      const hasDescription = firstCheckboxIndex > 1;
      const description = hasDescription ? headers[1] : "";
      const numOptions = checkboxHeaders.length;
      
      return (
        <div key={index} className="my-1 p-3 bg-white border rounded hover:bg-gray-50">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col md:flex-row md:items-start gap-2">
              <span className="text-[#3b3b3b] text-sm font-medium min-w-[200px]">{itemName}</span>
              {hasDescription && description && (
                <span className="text-gray-500 text-xs flex-1">{description}</span>
              )}
            </div>
            <div className="flex gap-3 items-center justify-end md:justify-start md:ml-[200px]">
              {checkboxHeaders.map((_, optIdx) => {
                const optionNum = optIdx + 1;
                return (
                  <label key={optIdx} className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      name={`${tableId}_skill_selection`}
                      checked={responses[`${tableId}_selection`] === optionNum.toString()}
                      onChange={() => handleResponseChange(`${tableId}_selection`, optionNum.toString())}
                      className="w-5 h-5 accent-[#00AEEF] cursor-pointer"
                      style={{pointerEvents: 'auto', position: 'relative', zIndex: 2}}
                    />
                    <span className="text-sm text-gray-600 font-medium">{optionNum}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      );
    }
    
    // Single checkbox tool table: ['Tool Name', '☐', '1-5', 'D/W/M/R', '']
    // Has exactly 1 checkbox + rating/frequency columns
    const isSingleCheckboxToolTable = style === "standard" && 
      rows.length === 0 && 
      checkboxHeaders.length === 1 &&
      headers.some(h => h === '1-5' || h.includes('1-5')) &&
      headers.some(h => h.includes('D/W/M') || h.includes('Freq'));
    
    if (isSingleCheckboxToolTable) {
      const toolName = headers[0] || "";
      
      return (
        <div key={index} className="my-1 p-3 bg-white border rounded hover:bg-gray-50">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <span className="text-[#3b3b3b] text-sm font-medium min-w-[200px] flex-shrink-0">{toolName}</span>
            <div className="flex items-center gap-4 flex-wrap">
              {/* I Use checkbox */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={responses[`${tableId}_use`] === true}
                  onChange={(e) => handleResponseChange(`${tableId}_use`, e.target.checked)}
                  className="w-5 h-5 accent-[#00AEEF] cursor-pointer"
                />
                <span className="text-sm text-gray-600">I Use</span>
              </label>
              
              {/* Rating 1-5 */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">Rating:</span>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  className="w-14 h-8 text-center text-sm"
                  value={responses[`${tableId}_rating`] || ""}
                  onChange={(e) => handleResponseChange(`${tableId}_rating`, e.target.value)}
                  placeholder="1-5"
                />
              </div>
              
              {/* Frequency D/W/M/R */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">Freq:</span>
                <Input
                  className="w-16 h-8 text-center text-sm"
                  value={responses[`${tableId}_freq`] || ""}
                  onChange={(e) => handleResponseChange(`${tableId}_freq`, e.target.value)}
                  placeholder="D/W/M"
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Rating table with specific columns
    if (isRatingTable) {
      return (
        <div key={index} className="my-4 overflow-x-auto">
          {hasNumberedColumns && (
            <p className="text-sm text-blue-600 mb-2 italic">
              💡 To rate each skill, type "X" in the column (1-5) that matches your proficiency level.
            </p>
          )}
          <table className="w-full border-collapse text-sm table-fixed">
            <thead>
              <tr className="bg-[#00AEEF]/10">
                {headers.map((h, idx) => {
                  const headerLower = h.toLowerCase();
                  // Make Description column wider, other columns narrower
                  const isDescColumn = headerLower.includes('description');
                  const isSkillColumn = headerLower.includes('skill') || headerLower.includes('competency');
                  const isNarrowColumn = headerLower.includes('rating') || headerLower.includes('freq') || headerLower === '1' || headerLower === '2' || headerLower === '3' || headerLower === '4' || headerLower === '5' || headerLower.includes('i use') || headerLower === 'use';
                  
                  let widthClass = '';
                  if (isDescColumn) widthClass = 'w-[40%]';
                  else if (isSkillColumn) widthClass = 'w-[20%]';
                  else if (isNarrowColumn) widthClass = 'w-[8%]';
                  
                  return (
                    <th key={idx} className={`p-2 text-left font-medium text-[#3b3b3b] border border-gray-200 text-xs ${widthClass}`}>
                      {h}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <tr key={rowIdx} className={rowIdx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  {row.map((cell, colIdx) => {
                    const headerLower = (headers[colIdx] || '').toLowerCase();
                    const cellStr = String(cell || '');
                    const isUseColumn = headerLower.includes('i use') || headerLower === 'use';
                    // Numbered columns (1,2,3,4,5) should allow typing the rating number
                    const isNumberedColumn = ['1', '2', '3', '4', '5'].includes(headerLower.trim());
                    const isRatingColumn = headerLower.includes('rating') || isNumberedColumn;
                    const isFreqColumn = headerLower.includes('freq');
                    const isPercentColumn = headerLower.includes('%') || cellStr.includes('%') || cellStr.includes('_____');
                    const isDescColumn = headerLower.includes('description');
                    
                    return (
                      <td key={colIdx} className={`p-2 border border-gray-200 ${isDescColumn ? 'whitespace-normal' : ''}`}>
                        {colIdx === 0 && !isPercentColumn ? (
                          // First column - tool/skill name (display only) unless it's a percent field
                          <span className="text-[#3b3b3b] text-sm">{cell}</span>
                        ) : colIdx === 1 && isDescColumn ? (
                          // Description column - display full text, allow wrapping
                          <span className="text-[#3b3b3b] text-sm block">{cell}</span>
                        ) : cellStr === '☐' || cellStr.trim() === '☐' ? (
                          // Cell contains checkbox symbol - render as radio button for row selection
                          <div className="flex justify-center">
                            <input
                              type="radio"
                              name={`${tableId}_row_${rowIdx}`}
                              checked={responses[`${tableId}_${rowIdx}_selection`] === colIdx.toString()}
                              onChange={() => handleResponseChange(`${tableId}_${rowIdx}_selection`, colIdx.toString())}
                              className="w-4 h-4 accent-[#00AEEF] cursor-pointer"
                            />
                          </div>
                        ) : isNumberedColumn ? (
                          // Numbered columns (1-5) - show input to type "X" to select
                          <div className="flex justify-center">
                            <Input
                              className="w-12 h-8 text-center text-sm"
                              value={responses[`${tableId}_${rowIdx}_${colIdx}`] || ""}
                              onChange={(e) => handleResponseChange(`${tableId}_${rowIdx}_${colIdx}`, e.target.value)}
                              placeholder="X"
                              maxLength={1}
                            />
                          </div>
                        ) : isUseColumn ? (
                          <Checkbox
                            checked={responses[`${tableId}_${rowIdx}_${colIdx}`] === true}
                            onCheckedChange={(checked) => handleResponseChange(`${tableId}_${rowIdx}_${colIdx}`, checked)}
                          />
                        ) : isRatingColumn ? (
                          <Input
                            type="number"
                            min="1"
                            max="5"
                            className="w-16 h-8 text-center text-sm"
                            value={responses[`${tableId}_${rowIdx}_${colIdx}`] || ""}
                            onChange={(e) => handleResponseChange(`${tableId}_${rowIdx}_${colIdx}`, e.target.value)}
                            placeholder="1-5"
                          />
                        ) : isFreqColumn ? (
                          <Input
                            className="w-20 h-8 text-center text-sm"
                            value={responses[`${tableId}_${rowIdx}_${colIdx}`] || ""}
                            onChange={(e) => handleResponseChange(`${tableId}_${rowIdx}_${colIdx}`, e.target.value)}
                            placeholder="D/W/M/R"
                          />
                        ) : isPercentColumn ? (
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            className="w-20 h-8 text-center text-sm"
                            value={responses[`${tableId}_${rowIdx}_${colIdx}`] || ""}
                            onChange={(e) => handleResponseChange(`${tableId}_${rowIdx}_${colIdx}`, e.target.value)}
                            placeholder="%"
                          />
                        ) : (
                          <Input
                            className="h-8 text-sm"
                            value={responses[`${tableId}_${rowIdx}_${colIdx}`] || cell || ""}
                            onChange={(e) => handleResponseChange(`${tableId}_${rowIdx}_${colIdx}`, e.target.value)}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Yellow row for custom entries */}
              <tr className="bg-yellow-50">
                {headers.map((_, colIdx) => (
                  <td key={colIdx} className="p-2 border border-gray-200">
                    <Input
                      className="h-8 text-sm bg-yellow-50"
                      value={responses[`${tableId}_custom_${colIdx}`] || ""}
                      onChange={(e) => handleResponseChange(`${tableId}_custom_${colIdx}`, e.target.value)}
                      placeholder={colIdx === 0 ? "Add your own..." : ""}
                    />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      );
    }
    
    // Likert scale table
    if (isLikertTable) {
      return (
        <div key={index} className="my-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm table-fixed">
            <thead>
              <tr className="bg-[#00AEEF]/10">
                {headers.map((h, idx) => (
                  <th 
                    key={idx} 
                    className={`p-2 text-center font-medium text-[#3b3b3b] border border-gray-200 text-xs ${
                      idx === 0 ? 'w-[50%] text-left' : 'w-[10%]'
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <tr key={rowIdx} className="hover:bg-gray-50">
                  {row.map((cell, colIdx) => (
                    <td 
                      key={colIdx} 
                      className={`p-2 border border-gray-200 ${
                        colIdx === 0 ? 'whitespace-normal break-words' : ''
                      }`}
                    >
                      {colIdx === 0 ? (
                        <span className="text-[#3b3b3b] text-sm block">{cell}</span>
                      ) : (
                        <div className="flex justify-center">
                          <input
                            type="radio"
                            name={`${tableId}_row_${rowIdx}`}
                            checked={responses[`${tableId}_${rowIdx}_likert`] === colIdx.toString()}
                            onChange={() => handleResponseChange(`${tableId}_${rowIdx}_likert`, colIdx.toString())}
                            className="w-4 h-4"
                          />
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    
    // Standard table with just text (like certification text) - display as paragraph
    if (style === "standard" && headers.length === 1 && (!rows || rows.length === 0)) {
      return (
        <div key={index} className="my-4 bg-gray-50 p-4 rounded-lg">
          <p className="text-[#3b3b3b] leading-relaxed">{headers[0]}</p>
        </div>
      );
    }
    
    // ===== Performance Snapshot table (Convention 13) =====
    // 4-column tables with year headers (e.g. 2022, 2023, 2024, 2025) → read-only display
    const isYearHeader = (h) => /^\d{4}$/.test((h || '').trim());
    const isPerformanceSnapshot = headers.length >= 3 && headers.every(isYearHeader);
    if (isPerformanceSnapshot) {
      return (
        <div key={index} className="my-4">
          <div className="overflow-x-auto rounded-lg border border-blue-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#D5E8F0]">
                  {headers.map((h, idx) => (
                    <th key={idx} className="px-4 py-2.5 text-center font-semibold text-[#1F4E79] border-r last:border-r-0 border-blue-200">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? rows.map((row, rowIdx) => (
                  <tr key={rowIdx} className="bg-white">
                    {row.map((cell, colIdx) => (
                      <td key={colIdx} className="px-4 py-2.5 text-center text-gray-600 border-r last:border-r-0 border-gray-200">{cell || '—'}</td>
                    ))}
                  </tr>
                )) : (
                  <tr className="bg-white">
                    {headers.map((_, idx) => (
                      <td key={idx} className="px-4 py-2.5 text-center text-gray-400 italic border-r last:border-r-0 border-gray-200">No data</td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    
    // ===== Profile/Education key-value table (Convention 6) =====
    // 2-column tables where the first cell is a known profile label (Education, Certifications, etc.)
    // These should be read-only display, NOT data entry
    const profileDisplayLabels = ['education', 'certifications', 'full name', 'email', 'direct manager',
      'job title', 'department', 'location', 'operating entity', 'operating entities', 'hire date',
      'assessment id', 'manager email', 'grade', 'work arrangement'];
    const firstHeaderLower = (headers[0] || '').toLowerCase().trim();
    const isProfileDisplayTable = headers.length === 2 && 
      profileDisplayLabels.includes(firstHeaderLower) &&
      (style === 'standard' || style === 'data_entry' || style === 'key_value');
    if (isProfileDisplayTable) {
      const allKVRows = [headers, ...rows];
      return (
        <div key={index} className="my-4">
          <div className="grid grid-cols-1 gap-3">
            {allKVRows.map((row, rowIdx) => {
              if (row.length >= 2 && row[0]) {
                const label = row[0].trim();
                const value = (row[1] || '').trim();
                if (!value || value === '—' || value === 'No records') {
                  return (
                    <div key={rowIdx} className="flex items-start bg-white border rounded-lg p-3">
                      <span className="font-medium text-[#1F4E79] min-w-[140px] text-sm">{label}</span>
                      <span className="text-gray-400 text-sm italic">No records</span>
                    </div>
                  );
                }
                // Split semicolons into individual items for long values
                const items = value.includes(';') ? value.split(';').map(s => s.trim()).filter(Boolean) : null;
                return (
                  <div key={rowIdx} className="flex items-start bg-white border rounded-lg p-3">
                    <span className="font-medium text-[#1F4E79] min-w-[140px] text-sm flex-shrink-0">{label}</span>
                    {items ? (
                      <ul className="text-gray-700 text-sm space-y-1 ml-2">
                        {items.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-[#00AEEF] mt-1.5 w-1.5 h-1.5 rounded-full bg-current flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-gray-700 text-sm ml-2">{value}</span>
                    )}
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      );
    }
    
    // Check if this is a percentage table (has "%" in headers or cells)
    const isPercentageTable = headers.some(h => h?.includes('%') || h?.includes('_____')) ||
      rows.some(row => row.some(cell => cell?.includes('%') || cell?.includes('_____')));
    
    // Generic editable table - for data entry tables with meaningful headers
    // Skip adding extra rows if it's a display-only table
    const shouldAddExtraRows = (style === "data_entry" && !headersStr.includes('manager certification')) || 
      (headers.length > 1 && headers.every(h => h && h.length < 50) && !isPercentageTable && !isProfileTable && !isICAREGrid && !isScoringTable);
    
    // Get the current number of extra rows for this table (stored in responses)
    const extraRowCountKey = `${tableId}_extra_row_count`;
    const currentExtraRows = parseInt(responses[extraRowCountKey]) || Math.max(3, rows.length);
    const maxRows = 50;
    
    // Function to add more rows
    const addMoreRows = () => {
      const newCount = Math.min(currentExtraRows + 3, maxRows);
      handleResponseChange(extraRowCountKey, newCount.toString());
    };
    
    return (
      <div key={index} className="my-4 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          {/* For percentage tables, render first row as data row, not header */}
          {isPercentageTable ? (
            <tbody>
              {/* First row (from headers) */}
              <tr className="bg-[#00AEEF]/10">
                {headers.map((h, colIdx) => {
                  const isPercentCell = h?.includes('%') || h?.includes('_____');
                  return (
                    <td key={colIdx} className="p-2 border border-gray-200">
                      {isPercentCell ? (
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          className="w-20 h-8 text-center text-sm"
                          value={responses[`${tableId}_header_${colIdx}`] || ""}
                          onChange={(e) => handleResponseChange(`${tableId}_header_${colIdx}`, e.target.value)}
                          placeholder="%"
                        />
                      ) : (
                        <span className="font-medium text-[#3b3b3b]">{h}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
              {/* Data rows */}
              {rows.map((row, rowIdx) => {
                // Skip TOTAL row - display only
                const isTotal = row[0]?.toLowerCase() === 'total';
                return (
                  <tr key={rowIdx} className={isTotal ? "bg-gray-100 font-medium" : "hover:bg-gray-50"}>
                    {row.map((cell, colIdx) => {
                      const isPercentCell = cell?.includes('%') || cell?.includes('_____');
                      return (
                        <td key={colIdx} className="p-2 border border-gray-200">
                          {isTotal ? (
                            <span className="text-[#3b3b3b]">{cell}</span>
                          ) : isPercentCell ? (
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              className="w-20 h-8 text-center text-sm"
                              value={responses[`${tableId}_${rowIdx}_${colIdx}`] || ""}
                              onChange={(e) => handleResponseChange(`${tableId}_${rowIdx}_${colIdx}`, e.target.value)}
                              placeholder="%"
                            />
                          ) : (
                            <span className="text-[#3b3b3b]">{cell}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          ) : (
            <>
              <thead>
                <tr className="bg-[#00AEEF]/10">
                  {headers.map((h, idx) => (
                    <th key={idx} className="p-2 text-left font-medium text-[#3b3b3b] border border-gray-200">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Render rows up to the current count */}
                {[...Array(currentExtraRows)].map((_, rowIdx) => {
                  const existingRow = rows[rowIdx];
                  return (
                    <tr key={rowIdx} className="hover:bg-gray-50">
                      {headers.map((h, colIdx) => {
                        const existingValue = existingRow?.[colIdx] || "";
                        return (
                          <td key={colIdx} className="p-1 border border-gray-200">
                            <Input
                              className="h-9 text-sm"
                              value={responses[`${tableId}_${rowIdx}_${colIdx}`] !== undefined ? responses[`${tableId}_${rowIdx}_${colIdx}`] : existingValue}
                              onChange={(e) => handleResponseChange(`${tableId}_${rowIdx}_${colIdx}`, e.target.value)}
                              placeholder={h || "..."}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </>
          )}
        </table>
        {/* Add More Rows button for data entry tables */}
        {shouldAddExtraRows && currentExtraRows < maxRows && (
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={addMoreRows}
              className="text-sm text-[#00AEEF] hover:text-[#0090c5] flex items-center gap-1 px-3 py-1.5 border border-[#00AEEF]/30 rounded-md hover:bg-[#00AEEF]/5 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add More Rows
            </button>
            <span className="text-xs text-gray-400">
              {currentExtraRows} of {maxRows} rows
            </span>
          </div>
        )}
      </div>
    );
  };

  // Render a single content item
  const renderContentItem = (item, index) => {
    switch (item.type) {
      case "title":
        if (item.level === 1) {
          return <h1 key={index} className="text-4xl font-bold text-[#00AEEF] text-center mt-8">{item.text}</h1>;
        }
        if (item.level === 2) {
          return <h2 key={index} className="text-2xl font-semibold text-[#3b3b3b] text-center">{item.text}</h2>;
        }
        return <h3 key={index} className="text-xl text-[#3b3b3b] text-center mb-6">{item.text}</h3>;
      
      case "section_header":
        if (item.style === "certification") {
          return (
            <div key={index} className="mt-12 mb-4">
              <h2 className="text-2xl font-bold text-[#63C465] border-b-2 border-[#63C465] pb-2">
                {item.text}
              </h2>
            </div>
          );
        }
        // Parse section header text to extract label and title
        const shTextMatch = (item.text || '').match(/^(SECTION\s+\S+)\s*[—–:-]?\s*(.*)$/i);
        if (shTextMatch) {
          return (
            <div key={index} className="my-6" data-testid={`section-header-${index}`}>
              <div className="bg-[#1B3A5C] text-white px-4 py-3 rounded-lg flex items-baseline gap-3">
                <span className="font-bold text-lg whitespace-nowrap">{shTextMatch[1].toUpperCase()}</span>
                {shTextMatch[2] && <span className="text-white/90 font-medium">{shTextMatch[2]}</span>}
              </div>
            </div>
          );
        }
        return (
          <div key={index} className="my-6" data-testid={`section-header-${index}`}>
            <div className="bg-[#1B3A5C] text-white px-4 py-3 rounded-lg">
              <span className="font-bold text-lg">{item.text}</span>
            </div>
          </div>
        );
      
      case "section":
        return (
          <div key={index} className="my-6" data-testid={`section-header-${index}`}>
            <div className={`bg-[#1B3A5C] text-white px-4 py-3 ${item.description ? 'rounded-t-lg' : 'rounded-lg'} flex items-baseline gap-3`}>
              <span className="font-bold text-lg whitespace-nowrap">SECTION {item.number}</span>
              <span className="text-white/90 font-medium">{item.title}</span>
            </div>
            {item.description && (
              <div className="bg-[#1B3A5C]/5 border border-t-0 border-[#1B3A5C]/20 px-4 py-2 rounded-b-lg">
                <p className="text-[#3b3b3b] text-sm italic">{item.description}</p>
              </div>
            )}
          </div>
        );
      
      case "subsection":
        return (
          <div key={index} className="mt-8 mb-4">
            <h3 className="text-lg font-semibold text-[#3b3b3b] border-l-4 border-[#00AEEF] pl-3">
              {item.number} {item.title}
            </h3>
          </div>
        );
      
      case "question":
        // Detect TA-style question (has response_fields or scenario) vs SA-style question
        if (item.response_fields || item.scenario) {
          return (
            <TAQuestionRenderer
              key={index}
              item={item}
              index={index}
              responses={responses}
              onResponseChange={handleTrackedResponseChange}
              onPaste={handleTrackedPaste}
              voiceEnabled={isVoiceEnabled}
              token={accessToken}
              sectionId={item._sectionId || `section_${Math.floor(index / 10)}`}
            />
          );
        }

        const questionId = `q_${item.number ?? item.id ?? index}`;
        const hasOtherOption = item.options?.some(opt => opt.toLowerCase().startsWith('other'));
        const otherSelected = Array.isArray(responses[questionId]) && 
          responses[questionId].some(r => r.toLowerCase().startsWith('other'));
        
        // Check if a nearby content item is a table (which will provide input fields)
        // Look ahead up to 3 items, skipping instructions/paragraphs
        let hasTableAfter = false;
        for (let look = 1; look <= 3; look++) {
          const ahead = content[index + look];
          if (!ahead) break;
          if (ahead.type === 'table') { hasTableAfter = true; break; }
          if (ahead.type !== 'instruction' && ahead.type !== 'paragraph') break;
        }
        
        // Check if this question needs a textarea input
        // A question needs textarea if:
        // 1. It has input_type='textarea' OR input_type is not set (undefined/null)
        // 2. It doesn't have checkbox options
        // 3. It's not followed by ANY table (tables provide their own inputs - tool ratings, skill matrices, etc.)
        const hasOptions = item.options && item.options.length > 0;
        const needsTextarea = !hasOptions && !hasTableAfter && 
          (item.input_type === 'textarea' || item.input_type === undefined || item.input_type === null);
        
        // Check if this is a header-style question (no input needed)
        // Only treat as header if it ends with colon AND it's not a normal question
        const isHeaderQuestion = item.text?.endsWith(':') && 
          !hasTableAfter && 
          !hasOptions && 
          item.input_type !== 'textarea' &&
          item.input_type !== undefined;
        
        return (
          <div key={index} className="my-6">
            <div className="flex items-start gap-3 mb-3">
              <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#00AEEF] text-white text-sm font-bold flex items-center justify-center">
                {item.number}
              </span>
              <Label className={`text-[#3b3b3b] leading-relaxed pt-1 ${isHeaderQuestion ? 'font-semibold' : 'font-medium'}`}>
                {item.text}
              </Label>
            </div>
            
            {/* Render checkbox options if present */}
            {item.options && item.options.length > 0 ? (
              <div className="ml-11 space-y-2">
                {item.options.map((option, optIdx) => {
                  const isOtherOption = option.toLowerCase().startsWith('other');
                  return (
                    <div key={optIdx} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50" style={{position: 'relative', zIndex: 1}}>
                      <Checkbox
                        id={`${questionId}_${optIdx}`}
                        style={{pointerEvents: 'auto', position: 'relative', zIndex: 2}}
                        checked={(responses[questionId] || []).includes(option)}
                        onCheckedChange={(checked) => {
                          const current = responses[questionId] || [];
                          const newValue = checked 
                            ? [...current, option]
                            : current.filter(v => v !== option);
                          handleResponseChange(questionId, newValue);
                          // Clear the other text if unchecking Other
                          if (!checked && isOtherOption) {
                            handleResponseChange(`${questionId}_other`, "");
                          }
                        }}
                      />
                      <Label htmlFor={`${questionId}_${optIdx}`} className="cursor-pointer text-[#3b3b3b] text-sm">
                        {isOtherOption ? "Other" : option}
                      </Label>
                    </div>
                  );
                })}
                {/* Show text input when Other is selected */}
                {hasOtherOption && otherSelected && (
                  <div className="mt-3 ml-6">
                    <Input
                      className="w-full text-sm"
                      placeholder="Please specify..."
                      value={responses[`${questionId}_other`] || ""}
                      onChange={(e) => handleResponseChange(`${questionId}_other`, e.target.value)}
                      data-testid={`question-other-input-${item.number}`}
                    />
                  </div>
                )}
              </div>
            ) : needsTextarea ? (
              // Render textarea for questions that need text input
              <div className="ml-11" style={{position: 'relative', zIndex: 1}}>
                <Textarea
                  className="w-full min-h-[100px] text-sm"
                  style={{pointerEvents: 'auto', position: 'relative', zIndex: 2}}
                  placeholder="Enter your response here..."
                  value={responses[questionId] || ""}
                  onChange={(e) => handleResponseChange(questionId, e.target.value)}
                  data-testid={`question-input-${item.number}`}
                />
              </div>
            ) : null}
          </div>
        );
      
      case "checkbox_option":
        // Group adjacent checkbox_options into a radio group (single-select behavior)
        // Find the group: look backwards for the question, and forward for more checkboxes
        const isFirstInGroup = index === 0 || content[index - 1]?.type !== 'checkbox_option';
        
        if (!isFirstInGroup) {
          // Already rendered as part of a group by the first checkbox
          return null;
        }
        
        // Collect all adjacent checkbox_options
        const cbGroup = [item];
        let nextIdx = index + 1;
        while (nextIdx < content.length && content[nextIdx]?.type === 'checkbox_option') {
          cbGroup.push(content[nextIdx]);
          nextIdx++;
        }
        
        // Expand: if a single checkbox_option contains ☐ separators, split into multiple options
        const expandedOptions = [];
        for (const cb of cbGroup) {
          const cbText = (cb.text || '').trim();
          if (cbText.includes('☐')) {
            // Split by ☐ and trim
            const parts = cbText.split(/☐/).map(p => p.trim()).filter(p => p);
            for (const part of parts) {
              expandedOptions.push({ ...cb, text: part });
            }
          } else {
            expandedOptions.push(cb);
          }
        }
        const finalGroup = expandedOptions.length > 0 ? expandedOptions : cbGroup;
        
        // Find the question text (previous paragraph)
        let questionContext = '';
        for (let qi = index - 1; qi >= Math.max(0, index - 3); qi--) {
          if (content[qi]?.type === 'paragraph' && content[qi]?.text) {
            questionContext = content[qi].text;
            break;
          }
        }
        
        // Determine if this should be single-select (radio) or multi-select (checkbox)
        const qLower = questionContext.toLowerCase();
        const isMultiSelect = qLower.includes('select all') || qLower.includes('multiple') || qLower.includes('check all') || qLower.includes('select up to');
        const radioGroupId = `cbgroup_${index}`;
        
        // Check if any option has "specify" or "other" meaning conditional text area
        const hasConditionalText = finalGroup.some(cb => {
          const t = (cb.text || '').toLowerCase();
          return t.includes('specify') || t.includes('additional context') || t.includes('please explain');
        });
        
        if (isMultiSelect) {
          // Multi-select checkboxes
          return (
            <div key={index} className="space-y-1 ml-11">
              {finalGroup.map((cb, cbIdx) => {
                const cbId = `cb_${index + cbIdx}`;
                return (
                  <div key={cbIdx} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                    <Checkbox
                      id={cbId}
                      checked={responses[cbId] === true || responses[cbId] === 'true'}
                      onCheckedChange={(checked) => handleResponseChange(cbId, checked)}
                    />
                    <Label htmlFor={cbId} className="cursor-pointer text-[#3b3b3b] text-sm">{cb.text}</Label>
                  </div>
                );
              })}
            </div>
          );
        } else {
          // Single-select radio group
          const selectedValue = responses[radioGroupId] || '';
          return (
            <div key={index} className="space-y-1 ml-11" data-testid={`radio-group-${index}`}>
              {finalGroup.map((cb, cbIdx) => {
                const cbText = cb.text || '';
                const isConditional = cbText.toLowerCase().includes('specify') || 
                                      cbText.toLowerCase().includes('additional context') ||
                                      cbText.toLowerCase().includes('please explain');
                const isSelected = selectedValue === cbText;
                return (
                  <div key={cbIdx}>
                    <div className="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                      <input
                        type="radio"
                        id={`${radioGroupId}_${cbIdx}`}
                        name={radioGroupId}
                        checked={isSelected}
                        onChange={() => handleResponseChange(radioGroupId, cbText)}
                        className="w-4 h-4 accent-[#00AEEF] cursor-pointer"
                        data-testid={`radio-${index}-${cbIdx}`}
                      />
                      <label htmlFor={`${radioGroupId}_${cbIdx}`} className="cursor-pointer text-[#3b3b3b] text-sm">{cbText}</label>
                    </div>
                    {/* Conditional text area - shows when this option is selected and it's a "specify" type */}
                    {isConditional && isSelected && (
                      <div className="ml-9 mt-1 mb-2">
                        <Textarea
                          className="w-full min-h-[80px] text-sm"
                          placeholder="Please provide additional details..."
                          value={responses[`${radioGroupId}_detail`] || ""}
                          onChange={(e) => handleResponseChange(`${radioGroupId}_detail`, e.target.value)}
                          data-testid={`conditional-textarea-${index}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        }
      
      case "instruction":
        return (
          <div key={index} className="my-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">{item.text}</p>
          </div>
        );
      
      case "paragraph":
        // Check if paragraph contains inline blanks (underscores)
        const text = item.text || "";
        const hasInlineBlanks = text.includes('_____') || text.includes('____');
        
        if (hasInlineBlanks) {
          // If the entire paragraph is just underscores (blank line placeholder),
          // suppress it — the textarea already provides the input
          const trimmed = text.replace(/[_\s]/g, '');
          if (!trimmed) {
            return null;
          }
          
          // Split by blank patterns and create input fields for inline blanks
          const parts = text.split(/(_{4,})/g);
          let blankIndex = 0;
          
          return (
            <div key={index} className="my-3 flex flex-wrap items-center gap-1">
              {parts.map((part, partIdx) => {
                if (part.match(/_{4,}/)) {
                  // This is a blank - render as input
                  const inputId = `para_${index}_blank_${blankIndex}`;
                  blankIndex++;
                  return (
                    <Input
                      key={partIdx}
                      className="w-48 h-8 text-sm inline-block mx-1"
                      value={responses[inputId] || ""}
                      onChange={(e) => handleResponseChange(inputId, e.target.value)}
                      placeholder="Enter here..."
                      data-testid={inputId}
                    />
                  );
                } else if (part.trim()) {
                  // Regular text
                  return <span key={partIdx} className="text-[#3b3b3b]">{part}</span>;
                }
                return null;
              })}
            </div>
          );
        }
        
        // Check if this is a question paragraph that needs a text area below it
        // Only if the next content item is NOT a table (empty or otherwise) and NOT a checkbox_option
        const paraNextItem = content[index + 1];
        const nextIsTable = paraNextItem && paraNextItem.type === 'table';
        const nextIsCheckbox = paraNextItem && paraNextItem.type === 'checkbox_option';
        const isQuestionPara = /^Q\d|^q\d|question/i.test(text.trim()) || text.trim().endsWith('?');
        const isOpenEnded = text.toLowerCase().includes('describe') || text.toLowerCase().includes('explain') || 
                           text.toLowerCase().includes('comment') || text.toLowerCase().includes('elaborate');
        
        if ((isQuestionPara || isOpenEnded) && !nextIsTable && !nextIsCheckbox) {
          return (
            <div key={index} className="my-3">
              <p className="text-[#3b3b3b] leading-relaxed mb-2">{item.text}</p>
              <Textarea
                className="w-full min-h-[100px] text-sm ml-4"
                placeholder="Enter your response here..."
                value={responses[`para_${index}_response`] || ""}
                onChange={(e) => handleResponseChange(`para_${index}_response`, e.target.value)}
                data-testid={`question-textarea-${index}`}
              />
            </div>
          );
        }
        
        return <p key={index} className="my-3 text-[#3b3b3b] leading-relaxed">{item.text}</p>;
      
      // ===== TA/HOS Renderer Types =====
      case "code_block":
        return <CodeBlockRenderer item={item} index={index} />;

      case "code_input":
        return (
          <CodeInputRenderer
            item={item}
            index={index}
            responses={responses}
            onResponseChange={handleTrackedResponseChange}
            onPaste={handleTrackedPaste}
          />
        );

      case "scenario_brief":
        return <ScenarioBriefRenderer item={item} index={index} />;

      case "structured_response":
        return (
          <StructuredResponseRenderer
            item={item}
            index={index}
            responses={responses}
            onResponseChange={handleTrackedResponseChange}
            onPaste={handleTrackedPaste}
            voiceEnabled={isVoiceEnabled}
            token={accessToken}
            sectionId={item._sectionId || `section_sr_${index}`}
          />
        );

      case "diagram_upload":
        return (
          <DiagramUploadRenderer
            item={item}
            index={index}
            responses={responses}
            onResponseChange={handleResponseChange}
          />
        );

      case "sandbox_simulation":
        return (
          <SandboxSimulation
            item={item}
            index={index}
            responses={responses}
            onResponseChange={handleResponseChange}
            candidateId={candidateId}
            voiceEnabled={isVoiceEnabled}
            token={accessToken}
            sectionId={item._sectionId || `section_sandbox_${index}`}
            activityLogger={activity}
          />
        );

      case "table": {
        const tableResult = renderTable(item, index);
        const tblId = `table_${index}`;
        const tblHeadersStr = (item.headers || []).join(' ').toLowerCase();
        const isTblDataEntry = (item.style === 'data_entry' || 
          item.table_type === 'certification_table' ||
          tblHeadersStr.includes('certification name') ||
          tblHeadersStr.includes('issuing body') ||
          tblHeadersStr.includes('issuing organization') ||
          tblHeadersStr.includes('credential') ||
          ((item.headers || []).length >= 2 && (tblHeadersStr.includes('name') && (tblHeadersStr.includes('date') || tblHeadersStr.includes('body') || tblHeadersStr.includes('expir'))))) &&
          !tblHeadersStr.includes('manager certification');
        
        // Exclude read-only profile/display tables from data entry treatment
        const profileDisplayLabelsCheck = ['education', 'certifications', 'full name', 'email', 'direct manager',
          'job title', 'department', 'location', 'operating entity', 'operating entities', 'hire date',
          'assessment id', 'manager email', 'grade', 'work arrangement'];
        const tblFirstHeader = ((item.headers || [])[0] || '').toLowerCase().trim();
        const isYearHeaderTbl = (item.headers || []).length >= 3 && (item.headers || []).every(h => /^\d{4}$/.test((h || '').trim()));
        const isProfileDisplay = (item.headers || []).length === 2 && profileDisplayLabelsCheck.includes(tblFirstHeader);
        const shouldShowAddRows = isTblDataEntry && !isYearHeaderTbl && !isProfileDisplay;
        
        if (shouldShowAddRows) {
          const currentExtra = extraTableRows[tblId] || 0;
          const currentTotal = Math.max((item.rows || []).length, 20) + currentExtra;
          return (
            <div key={`table-wrapper-${index}`}>
              {tableResult}
              <div className="flex items-center justify-between ml-11 mt-2 mb-4">
                <span className="text-xs text-gray-500">{currentTotal} rows available</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addTableRows(tblId, currentTotal, 5)}
                  className="text-[#00AEEF] border-[#00AEEF] hover:bg-[#00AEEF]/10"
                  data-testid={`add-rows-btn-${index}`}
                >
                  + Add More Rows
                </Button>
              </div>
            </div>
          );
        }
        return tableResult;
      }
      
      default:
        return null;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#00AEEF] mx-auto mb-4" />
          <p className="text-gray-600">Loading assessment...</p>
        </div>
      </div>
    );
  }

  // Paused assessment overlay
  if (assessmentData?.paused || assessmentData?.status === 'paused') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4" data-testid="assessment-paused-overlay">
        <div className="max-w-lg w-full">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-10 text-center shadow-2xl">
            <div className="w-20 h-20 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-amber-400" />
            </div>
            <h1 className="text-3xl font-bold text-white">Currently Unavailable</h1>
          </div>
        </div>
      </div>
    );
  }

  // Error state - show success message if assessment was already completed
  if (error) {
    const isCompletedMessage = error.toLowerCase().includes('already been completed') || 
                                error.toLowerCase().includes('already submitted');
    
    if (isCompletedMessage) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <Card className="max-w-lg">
            <CardContent className="pt-8 text-center">
              <div className="w-16 h-16 bg-[#63C465] rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#3b3b3b] mb-3">Assessment Already Submitted!</h2>
              <p className="text-gray-600">
                This assessment has already been completed. Thank you for your participation in Sagicor's technology transformation.
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    const isDisabled = error.includes("no longer active");
    if (isDisabled) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-7 h-7 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2" data-testid="assessment-disabled-title">Assessment Link Inactive</h2>
              <p className="text-gray-600 mb-4">This assessment link is no longer active. A new link will be shared with you shortly.</p>
              <p className="text-sm text-gray-500">For questions, please contact <a href="mailto:helpsagicor@thcohq.com" className="text-[#00AEEF] underline">helpsagicor@thcohq.com</a></p>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Unable to Load Assessment</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Submitted state
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-lg">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-[#63C465] rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#3b3b3b] mb-3">Assessment Submitted!</h2>
            <p className="text-gray-600">
              Thank you for completing this assessment. Your input is valuable to Sagicor's technology transformation.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const doc = assessmentData?.template?.documentStructure;
  // Flatten content: if sections contain nested items, flatten them into a single array
  const content = (() => {
    let raw = [];
    if (doc?.content) raw = doc.content;
    else if (doc?.phases) raw = doc.phases.flatMap(p => p.items || []);
    else if (doc?.acts) raw = doc.acts.flatMap(a => a.items || []);
    
    // Flatten sections that have nested items (TA/HOS format)
    const flat = [];
    for (const item of raw) {
      if (item.type === "section" && item.items && Array.isArray(item.items)) {
        const sectionId = item.id || item.section_id || `section_${item.section_number || item.number || flat.length}`;
        // Add the section header itself
        flat.push({ ...item, type: "section", number: item.section_number || item.number });
        // Then add all nested items with section context
        for (const child of item.items) {
          flat.push({ ...child, _sectionId: sectionId });
        }
      } else {
        flat.push(item);
      }
    }
    return flat;
  })();

  // Parse acts for HOS form mode — group content by section
  const hosActs = (() => {
    if (!isHOSFormMode || !doc?.content) return [];
    return doc.content
      .filter(item => item.type === "section" && item.id && item.id.startsWith("hos_act"))
      .map((section, idx) => ({
        id: section.id,
        index: idx,
        title: section.title || `Act ${idx + 1}`,
        sectionNumber: section.section_number || idx + 1,
        items: section.items || [],
      }));
  })();

  // Parse phases for TA mode — group content by phase sections
  const taPhases = (() => {
    if (!isTAPhaseMode || !doc?.content) return [];
    return doc.content
      .filter(item => item.type === "section" && item.id && item.id.startsWith("ta_phase"))
      .map((section, idx) => ({
        id: section.id,
        index: idx,
        title: section.title || `Phase ${idx + 1}`,
        sectionNumber: section.section_number || idx + 1,
        items: section.items || [],
        timer: section.timer || null,
      }));
  })();

  // Dynamically count questions from content (works for both SA and MV forms)
  const questionCount = (() => {
    if (doc?.metadata?.total_questions > 0) return doc.metadata.total_questions;
    let count = 0;
    for (const item of content) {
      if (item.type === 'question') count++;
      else if (item.type === 'sandbox_simulation') count++;
      else if (item.type === 'structured_response') count++;
      else if (item.type === 'paragraph' && /^Q\d/i.test((item.text || '').trim())) count++;
      else if (item.type === 'table' && item.rows) {
        count += item.rows.filter(r => r[0] && /^Q\d/i.test((r[0] || '').trim())).length;
      }
    }
    return count;
  })();

  // Phase 3: Gate enforcement removed — HOS always accessible

  return (
    <div className="min-h-screen bg-gray-50">
      {/* QA Mode Banner */}
      {isQAMode && (
        <div className="bg-amber-500 text-white text-center py-2 px-4 text-sm font-medium" data-testid="qa-mode-banner">
          QA ACCESS MODE — Read-only view. Timers, saves, and submissions are disabled. Status: {assessmentData?.status}
        </div>
      )}
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {assessmentData?.candidate?.assessmentType === 'manager_validation' && assessmentData?.candidate?.managerName ? (
                <span className="font-medium text-[#1B3A5C]">
                  {assessmentData.candidate.managerName} — validating {assessmentData.candidate.fullName}
                </span>
              ) : (
                <><span className="font-medium">{questionCount}</span> Questions</>
              )}
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Progress value={progressPercentage} className="w-20 h-2" />
                <span className="text-sm text-gray-600">{progressPercentage}%</span>
              </div>
              
              {!isQAMode && (
                <>
                  {saving ? (
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                    </span>
                  ) : lastSaved ? (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <Check className="w-4 h-4" /> Saved
                    </span>
                  ) : null}
                  
                  <Button variant="outline" size="sm" onClick={handleManualSave} disabled={saving}>
                    <Save className="w-4 h-4 mr-1" /> Save
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Timer Bar for TA/HOS assessments (overall timer — not shown when per-phase/per-act timers are active, or in preview) */}
      {isTimedAssessment && candidateId && totalTimeSeconds && !isHOSFormMode && !(taPhases.length > 0) && !isPreviewMode && (
        <div className="sticky top-[52px] z-40 max-w-4xl mx-auto px-4 pt-2">
          <TimerBar
            candidateId={candidateId}
            sectionId="overall"
            timeLimitSeconds={totalTimeSeconds}
            deferred={true}
            activated={timerActivated}
            qaMode={isQAMode}
            onTimeout={() => {
              setSectionTimedOut(prev => ({ ...prev, overall: true }));
              activity.logTimerEvent("expired", "overall", { total_seconds: totalTimeSeconds });
              // Auto-save on timeout
              saveResponses(responses);
            }}
          />
        </div>
      )}

      {/* Main Content */}
      <style>{`
        table.likert-table,
        table[data-survey-table] {
          table-layout: fixed !important;
          width: 100% !important;
        }
        table.likert-table th:first-child,
        table.likert-table td:first-child,
        table[data-survey-table] th:first-child,
        table[data-survey-table] td:first-child {
          width: 75% !important;
          text-align: left !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          white-space: normal !important;
          padding-right: 8px !important;
          line-height: 1.4 !important;
        }
        table.likert-table th:not(:first-child),
        table.likert-table td:not(:first-child),
        table[data-survey-table] th:not(:first-child),
        table[data-survey-table] td:not(:first-child) {
          width: 5% !important;
          min-width: 28px !important;
          max-width: 36px !important;
          text-align: center !important;
          padding-left: 1px !important;
          padding-right: 1px !important;
          font-size: 0.65rem !important;
          overflow: hidden !important;
          box-sizing: border-box !important;
        }
        table.likert-table input[type="radio"],
        table[data-survey-table] input[type="radio"] {
          margin: 0 auto !important;
          display: block !important;
          width: 14px !important;
          height: 14px !important;
          cursor: pointer !important;
        }
        table[data-rating-grid] {
          table-layout: auto !important;
        }
        table[data-rating-grid] th:first-child,
        table[data-rating-grid] td:first-child {
          text-align: left !important;
          word-wrap: break-word !important;
          white-space: normal !important;
        }
        table[data-rating-grid] th:not(:first-child),
        table[data-rating-grid] td:not(:first-child) {
          text-align: center !important;
          font-size: 0.7rem !important;
        }
        table[data-rating-grid] input[type="radio"] {
          margin: 0 auto !important;
          display: block !important;
          width: 16px !important;
          height: 16px !important;
        }
      `}</style>
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Preview Mode Banner */}
        {isPreviewMode && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 mb-6 flex items-center gap-2" data-testid="preview-banner">
            <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
            <div>
              <span className="font-semibold text-amber-700 text-sm">Preview Mode</span>
              <span className="text-amber-600 text-sm ml-2">Timers are paused. All sections visible. Inputs are display-only.</span>
            </div>
          </div>
        )}

        {/* Candidate Profile Card — surfaces parsed profile info for SA/MV forms so candidate can verify reporting structure. */}
        {assessmentData?.candidate && assessmentData.candidate.assessmentType === 'self_assessment' && (
          <div className="bg-white border-2 border-[#1B3A5C]/20 rounded-lg p-5 mb-6" data-testid="profile-card">
            <h3 className="text-base font-bold text-[#1B3A5C] mb-4 flex items-center gap-2">
              <span className="inline-block w-1 h-5 bg-[#1B3A5C] rounded"></span>
              Your Profile & Reporting Structure
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div data-testid="profile-fullname">
                <div className="text-gray-500 text-xs uppercase tracking-wide">Full Name</div>
                <div className="text-[#3b3b3b] font-medium">{assessmentData.candidate.fullName || "—"}</div>
              </div>
              <div data-testid="profile-email">
                <div className="text-gray-500 text-xs uppercase tracking-wide">Email</div>
                <div className="text-[#3b3b3b] font-medium break-all">{assessmentData.candidate.email || "—"}</div>
              </div>
              <div data-testid="profile-jobtitle">
                <div className="text-gray-500 text-xs uppercase tracking-wide">Job Title</div>
                <div className="text-[#3b3b3b] font-medium">{assessmentData.candidate.jobTitle || "—"}</div>
              </div>
              <div data-testid="profile-department">
                <div className="text-gray-500 text-xs uppercase tracking-wide">Department</div>
                <div className="text-[#3b3b3b] font-medium">{assessmentData.candidate.department || "—"}</div>
              </div>
              <div data-testid="profile-manager">
                <div className="text-gray-500 text-xs uppercase tracking-wide">Direct Manager</div>
                <div className="text-[#3b3b3b] font-medium">
                  {assessmentData.candidate.managerName || "—"}
                  {assessmentData.candidate.managerTitle && (
                    <span className="text-gray-500 font-normal ml-2">({assessmentData.candidate.managerTitle})</span>
                  )}
                </div>
              </div>
              <div data-testid="profile-region">
                <div className="text-gray-500 text-xs uppercase tracking-wide">Region</div>
                <div className="text-[#3b3b3b] font-medium capitalize">{assessmentData.candidate.region || "—"}</div>
              </div>
              {assessmentData.candidate.hireDate && (
                <div data-testid="profile-hiredate">
                  <div className="text-gray-500 text-xs uppercase tracking-wide">Hire Date</div>
                  <div className="text-[#3b3b3b] font-medium">{assessmentData.candidate.hireDate}</div>
                </div>
              )}
              {assessmentData.candidate.yearsAtSagicor && (
                <div data-testid="profile-tenure">
                  <div className="text-gray-500 text-xs uppercase tracking-wide">Tenure</div>
                  <div className="text-[#3b3b3b] font-medium">{assessmentData.candidate.yearsAtSagicor}</div>
                </div>
              )}
              {assessmentData.candidate.jobGrade && (
                <div data-testid="profile-jobgrade">
                  <div className="text-gray-500 text-xs uppercase tracking-wide">Job Grade</div>
                  <div className="text-[#3b3b3b] font-medium">{assessmentData.candidate.jobGrade}</div>
                </div>
              )}
              {assessmentData.candidate.location && (
                <div data-testid="profile-location">
                  <div className="text-gray-500 text-xs uppercase tracking-wide">Location</div>
                  <div className="text-[#3b3b3b] font-medium">{assessmentData.candidate.location}</div>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-4 italic">
              If any of these details are incorrect, please contact THCO Support before submitting your assessment.
            </p>
          </div>
        )}

        {/* TA Phase Mode: Render phases with per-phase timers and independent locking */}
        {isTAPhaseMode && taPhases.length > 0 ? (
          <>
            {taPhases.map((phase, phaseIdx) => {
              const isPhaseLocked = !isPreviewMode && !!sectionTimedOut[phase.id];
              const isPhaseActive = isPreviewMode || (phaseIdx === activePhaseIndex && !isPhaseLocked);
              const isPhasePending = !isPreviewMode && phaseIdx > activePhaseIndex && !isPhaseLocked;
              const phaseTimerSeconds = phase.timer?.duration_seconds || 720;

              return (
                <div key={phase.id} className="mb-10" data-testid={`ta-phase-${phase.id}`}>
                  {/* Phase Header */}
                  <div className={`rounded-t-lg px-5 py-4 ${
                    isPhaseLocked ? "bg-gray-200" : isPhaseActive ? "bg-gradient-to-r from-[#1B3A5C] to-[#2a5a8c]" : "bg-gray-100"
                  }`}>
                    <div className="flex items-center justify-between">
                      <h3 className={`font-bold text-lg ${
                        isPhaseLocked ? "text-gray-500" : isPhaseActive ? "text-white" : "text-gray-400"
                      }`}>
                        {phase.title}
                      </h3>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs ${isPhaseActive ? "text-white/60" : "text-gray-400"}`}>
                          {phase.items.filter(i => i.type === "question").length} questions
                        </span>
                        {!isPreviewMode && (
                          <span className={`text-xs ${isPhaseActive ? "text-white/60" : "text-gray-400"}`}>
                            {Math.floor(phaseTimerSeconds / 60)} min
                          </span>
                        )}
                        {isPreviewMode && (
                          <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded">
                            {Math.floor(phaseTimerSeconds / 60)}:{String(phaseTimerSeconds % 60).padStart(2, "0")}
                          </span>
                        )}
                        {isPhaseLocked && (
                          <span className="text-xs font-medium text-gray-500 bg-white/80 px-2 py-0.5 rounded" data-testid={`phase-locked-badge-${phase.id}`}>
                            Locked
                          </span>
                        )}
                        {isPhasePending && (
                          <span className="text-xs text-gray-400">Starts after Phase {phaseIdx}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Per-Phase Timer — sticky at top while scrolling */}
                  {isPhaseActive && !isPreviewMode && candidateId && (
                    <div className="sticky top-[52px] z-40 px-4 pt-3 bg-white border-b border-gray-100" style={{ isolation: 'isolate' }}>
                      <TimerBar
                        candidateId={candidateId}
                        sectionId={phase.id}
                        timeLimitSeconds={phaseTimerSeconds}
                        deferred={true}
                        activated={timerActivated}
                        qaMode={isQAMode}
                        onTimeout={() => {
                          setSectionTimedOut(prev => ({ ...prev, [phase.id]: true }));
                          saveResponses(responses);
                          activity.logPhaseSubmit(phase.id, phaseIdx, "timeout");
                          if (phaseIdx + 1 < taPhases.length) {
                            setActivePhaseIndex(phaseIdx + 1);
                          }
                        }}
                      />
                    </div>
                  )}
                  {isPhaseLocked && (
                    <div className="px-4 pt-3">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 flex items-center gap-2" data-testid={`timer-expired-${phase.id}`}>
                        <AlertCircle className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-500">Time expired — responses saved</span>
                      </div>
                    </div>
                  )}

                  {/* Phase Content */}
                  <div className={`border border-t-0 rounded-b-lg p-4 relative ${isPhasePending ? "overflow-hidden" : ""} ${isPhaseLocked ? "bg-gray-50" : isPhasePending ? "bg-gray-50/50" : "bg-white"}`}>
                    {/* Frosted glass blur overlay for pending phases */}
                    {isPhasePending && (
                      <div className="absolute inset-0 z-10 bg-white/40 backdrop-blur-md flex items-center justify-center rounded-b-lg" data-testid={`phase-blur-overlay-${phase.id}`}>
                        <div className="text-center bg-white/80 rounded-xl px-6 py-4 shadow-sm">
                          <Lock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500 font-medium text-sm">Complete Phase {phaseIdx} to unlock</p>
                        </div>
                      </div>
                    )}
                    {phase.items.map((item, itemIdx) => {
                      if (item.type === "question") {
                        return (
                          <TAQuestionRenderer
                            key={item.id || itemIdx}
                            item={item}
                            index={itemIdx}
                            responses={responses}
                            onResponseChange={handleTrackedResponseChange}
                            onPaste={handleTrackedPaste}
                            voiceEnabled={isVoiceEnabled}
                            token={accessToken}
                            sectionId={phase.id}
                            locked={isPhaseLocked || isPhasePending || isPreviewMode}
                          />
                        );
                      }
                      return renderContentItem(item, itemIdx);
                    })}
                    {/* Submit Phase Button — only for active, non-preview phases */}
                    {isPhaseActive && !isPreviewMode && (
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <Button
                          onClick={() => handlePhaseSubmit(phase.id, phaseIdx, taPhases.length)}
                          className="w-full bg-[#1B3A5C] hover:bg-[#2a5a8c] h-12 text-base font-semibold"
                          data-testid={`submit-phase-btn-${phase.id}`}
                        >
                          <Send className="w-5 h-5 mr-2" /> Submit Phase {phaseIdx + 1}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        ) : isHOSFormMode && hosActs.length > 0 ? (
          <>
            {hosActs.map((act, actIdx) => {
              const isActLocked = !isPreviewMode && !!sectionTimedOut[act.id];
              const isActActive = isPreviewMode || (actIdx === activeActIndex && !isActLocked);
              const isActPending = !isPreviewMode && actIdx > activeActIndex && !isActLocked;

              return (
                <div key={act.id} className="mb-8" data-testid={`hos-act-${act.id}`}>
                  {/* Act Header */}
                  <div className={`rounded-t-lg px-5 py-3 ${
                    isActLocked ? "bg-gray-200" : isActActive ? "bg-[#1B3A5C]" : "bg-gray-100"
                  }`}>
                    <div className="flex items-center justify-between">
                      <h3 className={`font-semibold ${
                        isActLocked ? "text-gray-500" : isActActive ? "text-white" : "text-gray-400"
                      }`}>
                        {act.title}
                      </h3>
                      {isPreviewMode && (
                        <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded">6:00</span>
                      )}
                      {isActLocked && (
                        <span className="text-xs font-medium text-gray-500 bg-white/80 px-2 py-0.5 rounded" data-testid={`act-locked-badge-${act.id}`}>
                          Locked
                        </span>
                      )}
                      {isActPending && (
                        <span className="text-xs text-gray-400">Starts after Act {actIdx}</span>
                      )}
                    </div>
                  </div>

                  {/* Per-Act Timer — sticky at top while scrolling */}
                  {isActActive && !isPreviewMode && candidateId && (
                    <div className="sticky top-[52px] z-40 px-4 pt-3 bg-white border-b border-gray-100" style={{ isolation: 'isolate' }}>
                      <TimerBar
                        candidateId={candidateId}
                        sectionId={act.id}
                        timeLimitSeconds={actTimeLimitSeconds}
                        deferred={true}
                        activated={timerActivated}
                        qaMode={isQAMode}
                        onTimeout={() => {
                          setSectionTimedOut(prev => ({ ...prev, [act.id]: true }));
                          saveResponses(responses);
                          activity.logPhaseSubmit(act.id, actIdx, "timeout");
                          if (actIdx + 1 < hosActs.length) {
                            setActiveActIndex(actIdx + 1);
                          }
                        }}
                      />
                    </div>
                  )}
                  {isActLocked && (
                    <div className="px-4 pt-3">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 flex items-center gap-2" data-testid={`timer-expired-${act.id}`}>
                        <AlertCircle className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-500">Time expired — responses saved</span>
                      </div>
                    </div>
                  )}

                  {/* Act Content */}
                  <div className={`border border-t-0 rounded-b-lg p-4 relative ${isActPending ? "overflow-hidden" : ""} ${isActLocked ? "bg-gray-50" : isActPending ? "bg-gray-50/50" : "bg-white"}`}>
                    {/* Frosted glass blur overlay for pending acts */}
                    {isActPending && (
                      <div className="absolute inset-0 z-10 bg-white/40 backdrop-blur-md flex items-center justify-center rounded-b-lg" data-testid={`act-blur-overlay-${act.id}`}>
                        <div className="text-center bg-white/80 rounded-xl px-6 py-4 shadow-sm">
                          <Lock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500 font-medium text-sm">Complete Act {actIdx} to unlock</p>
                        </div>
                      </div>
                    )}
                    {act.items.map((item, itemIdx) => {
                      const globalIndex = content.findIndex(c => c.id === item.id || (c._sectionId === act.id && c.type === item.type && content.indexOf(c) >= 0));
                      const renderIdx = globalIndex >= 0 ? globalIndex : itemIdx;

                      if (item.type === "scenario_brief") {
                        return <ScenarioBriefRenderer key={item.id || itemIdx} item={item} index={renderIdx} />;
                      }
                      if (item.type === "structured_response") {
                        return (
                          <StructuredResponseRenderer
                            key={item.id || itemIdx}
                            item={item}
                            index={renderIdx}
                            responses={responses}
                            onResponseChange={handleTrackedResponseChange}
                            onPaste={handleTrackedPaste}
                            voiceEnabled={isVoiceEnabled}
                            token={accessToken}
                            sectionId={act.id}
                            locked={isActLocked || isActPending || isPreviewMode}
                          />
                        );
                      }
                      return renderContentItem(item, renderIdx);
                    })}
                    {/* Submit Act Button — only for active, non-preview acts */}
                    {isActActive && !isPreviewMode && (
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <Button
                          onClick={() => handleActSubmit(act.id, actIdx, hosActs.length)}
                          className="w-full bg-[#1B3A5C] hover:bg-[#2a5a8c] h-12 text-base font-semibold"
                          data-testid={`submit-act-btn-${act.id}`}
                        >
                          <Send className="w-5 h-5 mr-2" /> Submit Act {actIdx + 1}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        ) : isBrowserSimulation ? (
          <>
            {/* Browser Simulation Mode — robust resolver for all JSON schema variants */}
            {(() => {
              const sandboxItem = doc?.content?.[0]?.items?.find(i =>
                i.type === 'sandbox_simulation' ||
                (i.type == null && Array.isArray(i.files) && i.files.length > 0)
              );
              const contentBlock = doc?.content?.[0];
              const env = sandboxItem?.environment;

              // Resolve files from 4 known schema locations (priority order)
              const rawFiles =
                (sandboxItem?.files?.length && sandboxItem.files) ||
                (env?.files?.length && env.files) ||
                (contentBlock?.files?.length && contentBlock.files) ||
                (doc?.files?.length && doc.files) ||
                [];

              // Resolve companion props — cascade through all possible containers
              const propSources = [sandboxItem, env, contentBlock, doc].filter(Boolean);
              const resolve = (key, fallback) => {
                for (const src of propSources) {
                  if (src[key] !== undefined && src[key] !== null) return src[key];
                }
                return fallback;
              };

              return (
                <SandboxSimulation
                  item={{
                    id: sandboxItem?.id || "browser_sim_main",
                    title: sandboxItem?.title || doc?.metadata?.title || doc?.metadata?.candidate_name || "Hands-On Simulation",
                    files: rawFiles.map(f => ({ ...f, path: f.path || f.filename })),
                    readme: resolve("readme", ""),
                    test_suite: resolve("test_suite", {}),
                    diagnostic_console: resolve("diagnostic_console", {}),
                    max_test_runs: resolve("max_test_runs", 3),
                  }}
                  index={0}
                  responses={responses}
                  onResponseChange={handleTrackedResponseChange}
                  candidateId={candidateId}
                  voiceEnabled={isVoiceEnabled}
                  token={accessToken}
                  sectionId="browser_sim"
                  activityLogger={activity}
                />
              );
            })()}
          </>
        ) : (
          <>
            {/* Standard rendering: Render all content in document order, excluding certification items */}
            {content.map((item, originalIndex) => {
              if (isCertificationItem(item, originalIndex, content)) return null;
              return renderContentItem(item, originalIndex);
            })}
          </>
        )}
        
        {/* Certification section - always show with proper form inputs */}
        <div className="mt-12 bg-green-50 border-2 border-[#63C465]/30 rounded-lg p-6" data-testid="certification-section">
          <h3 className="text-lg font-semibold text-[#3b3b3b] mb-4">CERTIFICATION</h3>
          <p className="text-[#3b3b3b] mb-6 leading-relaxed">
              I certify that the information provided in this {isTimedAssessment ? "assessment" : "self-assessment"} is accurate and complete to the best of my knowledge. I understand this information will be used for capability assessment and career development purposes.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-white rounded-lg border">
                <Checkbox
                  id="certification"
                  checked={certificationChecked}
                  onCheckedChange={(v) => { setCertificationChecked(v); activity.logCertification("checkbox_toggled", { checked: v }); }}
                />
                <Label htmlFor="certification" className="cursor-pointer text-[#3b3b3b]">
                  I certify that all information provided is accurate and complete
                </Label>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-[#3b3b3b] mb-2 block">Signature:</Label>
                <Input
                  value={signatureName}
                  onChange={(e) => { setSignatureName(e.target.value); activity.logCertification("signature_entered", { length: e.target.value.length }); }}
                  placeholder="Type your full name"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#3b3b3b] mb-2 block">Date:</Label>
                <Input
                  type="date"
                  value={signatureDate}
                  onChange={(e) => setSignatureDate(e.target.value)}
                />
              </div>
            </div>
            
            <Button
              onClick={handleSubmit}
              disabled={submitting || !certificationChecked || !signatureName.trim() || isQAMode}
              className="w-full bg-[#63C465] hover:bg-[#4fa651] h-12"
              data-testid="submit-assessment-btn"
            >
              {isQAMode ? (
                <>Submit Disabled (QA Mode)</>
              ) : submitting ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <><Send className="w-5 h-5 mr-2" /> Submit Assessment</>
              )}
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white mt-12 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">© 2026 THCO</p>
        </div>
      </footer>
    </div>
  );
}

// Export wrapped with MaintenanceOverlay
export default function DynamicAssessmentForm() {
  const { accessToken } = useParams();
  
  return (
    <MaintenanceOverlay assessmentToken={accessToken}>
      <DynamicAssessmentFormContent />
    </MaintenanceOverlay>
  );
}
